import type { Language } from '@respiria/shared';

/**
 * Lightweight i18n. No dependency: a flat catalogue of keys, each with its
 * translations grouped together so they're easy to keep in sync and to spot
 * what's missing. `{name}` placeholders are interpolated.
 *
 * Spanish (`es`) is the complete baseline. Resolution falls back es ← ca ←
 * ca-valencia, so Valencian only needs an entry where it differs from Catalan,
 * and any untranslated key shows Spanish instead of a blank or a raw key.
 *
 * Migration is progressive (docs/OPEN-QUESTIONS.md#idiomas): screens move to
 * `t()` in batches. Add keys here as each screen is migrated.
 */

/** Selectable languages, in the agreed rollout order, with their native names. */
export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'es', label: 'Español' },
  { code: 'ca', label: 'Català' },
  { code: 'ca-valencia', label: 'Valencià' },
  { code: 'en', label: 'English' },
  { code: 'hu', label: 'Magyar' },
  { code: 'eu', label: 'Euskara' },
  { code: 'it', label: 'Italiano' },
  { code: 'tr', label: 'Türkçe' },
];

/** Each language falls back to this one when a key is missing. es is the root. */
const FALLBACK: Partial<Record<Language, Language>> = { 'ca-valencia': 'ca' };

type Entry = Partial<Record<Language, string>>;

const STRINGS: Record<string, Entry> = {
  // Navigation
  'nav.home': { es: 'Inicio', ca: 'Inici', en: 'Home' },
  'nav.health': { es: 'Salud', ca: 'Salut', en: 'Health' },
  'nav.progress': { es: 'Progreso', ca: 'Progrés', en: 'Progress' },
  'nav.more': { es: 'Más', ca: 'Més', en: 'More' },
  'nav.craving': { es: 'Antojo', ca: 'Ganes', en: 'Craving' },

  // Settings
  'settings.language': { es: 'Idioma', ca: 'Idioma', en: 'Language' },
  'settings.currency': { es: 'Moneda', ca: 'Moneda', en: 'Currency' },
  'settings.currencyDesc': {
    es: 'La moneda en la que ves tus ahorros. Solo cambia el símbolo: no convierte importes.',
    ca: 'La moneda en què veus els teus estalvis. Només canvia el símbol: no converteix imports.',
    en: 'The currency your savings are shown in. Only the symbol changes — amounts aren’t converted.',
  },
  'settings.language.hint': {
    es: 'Algunas partes seguirán en español mientras terminamos de traducir.',
    ca: 'Algunes parts seguiran en espanyol mentre acabem de traduir.',
    en: 'Some parts stay in Spanish until translation is complete.',
  },

  // Common
  'common.day': { es: 'día', ca: 'dia', en: 'day' },
  'common.days': { es: 'días', ca: 'dies', en: 'days' },
  'common.saving': { es: 'Guardando…', ca: 'Desant…', en: 'Saving…' },
  'common.loading': { es: 'Cargando', ca: 'Carregant', en: 'Loading' },
  'common.save': { es: 'Guardar', ca: 'Desar', en: 'Save' },
  'common.share': { es: 'Compartir', ca: 'Compartir', en: 'Share' },
  'common.continue': { es: 'Seguir', ca: 'Continuar', en: 'Continue' },
  'common.modify': { es: 'Modificar', ca: 'Modificar', en: 'Edit' },
  'common.confirm': { es: 'Confirmar', ca: 'Confirmar', en: 'Confirm' },
  'common.cancel': { es: 'Cancelar', ca: 'Cancel·lar', en: 'Cancel' },

  // Tutorial / coach-marks (issue #57). Controls in es/ca/en; step copy es-only
  // for now (falls back to es in other languages until the translation pass).
  'tour.next': { es: 'Siguiente', ca: 'Següent', en: 'Next' },
  'tour.prev': { es: 'Atrás', ca: 'Enrere', en: 'Back' },
  'tour.skip': { es: 'Saltar', ca: 'Ometre', en: 'Skip' },
  'tour.done': { es: 'Entendido', ca: 'Entesos', en: 'Got it' },
  'tour.replay.title': { es: 'Tutoriales', ca: 'Tutorials', en: 'Tutorials' },
  'tour.replay.desc': {
    es: 'Vuelve a ver la guía de cada modalidad cuando quieras.',
    ca: 'Torna a veure la guia de cada modalitat quan vulguis.',
    en: 'Replay the guide for each mode whenever you want.',
  },
  'tour.replay.current': { es: 'actual', ca: 'actual', en: 'current' },
  'tour.mode.smoking': { es: 'Fumando', ca: 'Fumant', en: 'Smoking' },
  'tour.mode.quitting': { es: 'Dejándolo', ca: 'Deixant-ho', en: 'Quitting' },
  'tour.mode.maintaining': { es: 'Manteniéndote', ca: 'Mantenint-te', en: 'Maintaining' },
  'tour.outro.title': { es: '¡Listo!', ca: 'Llest!', en: 'All set!' },
  'tour.outro.body': { es: 'Puedes repetir este tutorial cuando quieras desde Ajustes.', ca: 'Pots repetir aquest tutorial quan vulguis des d’Ajustos.', en: 'You can replay this tutorial any time from Settings.' },

  'tour.smoking.intro.title': { es: '👋 Estás fumando', ca: '👋 Estàs fumant', en: '👋 You’re smoking' },
  'tour.smoking.intro.body': {
    es: 'Sin presión y sin culpa. Aquí mides tu consumo a tu ritmo y, cuando te sientas listo, das el paso.',
    ca: 'Sense pressió i sense culpa. Aquí mesures el teu consum al teu ritme i, quan et sentis a punt, fas el pas.',
    en: 'No pressure, no guilt. Here you track how much you smoke at your own pace and, when you feel ready, you take the step.',
  },
  'tour.smoking.hero.title': { es: 'Tu consumo de hoy', ca: 'El teu consum d’avui', en: 'What you’ve smoked today' },
  'tour.smoking.hero.body': {
    es: 'Registra cada cigarro con un toque y mira cuántos llevas hoy. Ponte un objetivo diario para ir bajando.',
    ca: 'Registra cada cigarret amb un toc i mira quants en portes avui. Posa’t un objectiu diari per anar baixant.',
    en: 'Log each cigarette with one tap and see how many you’ve had today. Set a daily target to bring it down.',
  },
  'tour.smoking.quitday.title': { es: 'Elige tu Día D', ca: 'Tria el teu Dia D', en: 'Pick your Quit Day' },
  'tour.smoking.quitday.body': {
    es: 'Cuando quieras, marca el día en que lo dejas: pasarás al modo «Dejándolo» y empezará tu cuenta atrás.',
    ca: 'Quan vulguis, marca el dia en què ho deixes: passaràs al mode «Deixant-ho» i començarà el teu compte enrere.',
    en: 'Whenever you want, set the day you quit: you’ll move to “Quitting” mode and your countdown begins.',
  },
  'tour.smoking.craving.title': { es: 'Un antojo', ca: 'Unes ganes', en: 'A craving' },
  'tour.smoking.craving.body': {
    es: 'Si te entran ganas de fumar, pulsa aquí: te acompañamos a superarlo en un momento.',
    ca: 'Si et vénen ganes de fumar, prem aquí: t’acompanyem a superar-les en un moment.',
    en: 'If the urge to smoke hits, tap here: we’ll get you through it in a moment.',
  },
  'tour.smoking.more.title': { es: 'Todo lo demás', ca: 'Tota la resta', en: 'Everything else' },
  'tour.smoking.more.body': { es: 'En «Más» tienes tu diario, los ajustes y el resto de la app.', ca: 'A «Més» hi tens el teu diari, els ajustos i la resta de l’app.', en: 'Under “More” you’ll find your journal, settings and the rest of the app.' },

  'tour.quitting.intro.title': { es: '💪 Lo estás dejando', ca: '💪 Ho estàs deixant', en: '💪 You’re quitting' },
  'tour.quitting.intro.body': {
    es: 'Aquí tienes tu racha, tu tratamiento y tu apoyo para los antojos. Un día a la vez.',
    ca: 'Aquí tens la teva ratxa, el teu tractament i el teu suport per a les ganes. Un dia rere l’altre.',
    en: 'Here’s your streak, your treatment and your support for cravings. One day at a time.',
  },
  'tour.quitting.hero.title': { es: 'Tu racha', ca: 'La teva ratxa', en: 'Your streak' },
  'tour.quitting.hero.body': {
    es: 'El tiempo que llevas sin fumar (o la cuenta atrás hasta tu Día D). Crece contigo.',
    ca: 'El temps que portes sense fumar (o el compte enrere fins al teu Dia D). Creix amb tu.',
    en: 'How long you’ve gone without smoking (or the countdown to your Quit Day). It grows with you.',
  },
  'tour.quitting.treatment.title': { es: 'Tu tratamiento', ca: 'El teu tractament', en: 'Your treatment' },
  'tour.quitting.treatment.body': {
    es: 'Sigue tu pauta y marca cada toma con un toque, sin salir de la pantalla de inicio.',
    ca: 'Segueix la teva pauta i marca cada presa amb un toc, sense sortir de la pantalla d’inici.',
    en: 'Follow your schedule and mark each dose with one tap, without leaving the home screen.',
  },
  'tour.quitting.checkin.title': { es: 'Check-in diario', ca: 'Check-in diari', en: 'Daily check-in' },
  'tour.quitting.checkin.body': {
    es: 'Cuéntanos cómo te sientes cada día. Ayuda a personalizar tu apoyo.',
    ca: 'Explica’ns com et sents cada dia. Ajuda a personalitzar el teu suport.',
    en: 'Tell us how you feel each day. It helps tailor your support.',
  },
  'tour.quitting.craving.title': { es: 'Antojo', ca: 'Ganes', en: 'Craving' },
  'tour.quitting.craving.body': {
    es: 'Pulsa aquí para superar unas ganas de fumar. Mantén pulsado para el modo emergencia. Una recaída nunca borra tu progreso.',
    ca: 'Prem aquí per superar unes ganes de fumar. Mantén premut per al mode emergència. Una recaiguda mai no esborra el teu progrés.',
    en: 'Tap here to get through an urge to smoke. Press and hold for emergency mode. A relapse never erases your progress.',
  },
  'tour.quitting.money.title': { es: 'Lo que ahorras', ca: 'El que estalvies', en: 'What you’re saving' },
  'tour.quitting.money.body': {
    es: 'El dinero que llevas ahorrado desde tu Día D. Un buen empujón los días difíciles.',
    ca: 'Els diners que portes estalviats des del teu Dia D. Una bona empenta els dies difícils.',
    en: 'The money you’ve saved since your Quit Day. A good push on the hard days.',
  },
  'tour.quitting.progress.title': { es: 'Tu progreso', ca: 'El teu progrés', en: 'Your progress' },
  'tour.quitting.progress.body': { es: 'Niveles, logros y tu evolución están en «Progreso».', ca: 'Nivells, assoliments i la teva evolució són a «Progrés».', en: 'Levels, achievements and how you’re doing live under “Progress”.' },

  'tour.maintaining.intro.title': { es: '🌱 Te mantienes', ca: '🌱 Et mantens', en: '🌱 You’re staying smoke-free' },
  'tour.maintaining.intro.body': {
    es: 'Ya lo dejaste. Esto te ayuda a seguir firme y a celebrar lo conseguido.',
    ca: 'Ja ho vas deixar. Això t’ajuda a seguir ferm i a celebrar el que has aconseguit.',
    en: 'You’ve already quit. This helps you stay firm and celebrate what you’ve achieved.',
  },
  'tour.maintaining.hero.title': { es: 'Tu racha sin fumar', ca: 'La teva ratxa sense fumar', en: 'Your smoke-free streak' },
  'tour.maintaining.hero.body': {
    es: 'Sigue creciendo. Aquí ves el tiempo y los días limpios acumulados.',
    ca: 'Segueix creixent. Aquí veus el temps i els dies nets acumulats.',
    en: 'It keeps growing. Here you see the time and the clean days you’ve piled up.',
  },
  'tour.maintaining.craving.title': { es: 'Si vuelve un antojo', ca: 'Si tornen les ganes', en: 'If a craving comes back' },
  'tour.maintaining.craving.body': {
    es: 'Pulsa aquí y lo superamos juntos. Un desliz no resetea tu histórico ni tus logros.',
    ca: 'Prem aquí i les superem junts. Una relliscada no reinicia el teu històric ni els teus assoliments.',
    en: 'Tap here and we’ll get through it together. A slip doesn’t reset your history or your achievements.',
  },
  'tour.maintaining.health.title': { es: 'Tu salud', ca: 'La teva salut', en: 'Your health' },
  'tour.maintaining.health.body': { es: 'Mira cómo se ha recuperado tu cuerpo en «Salud».', ca: 'Mira com s’ha recuperat el teu cos a «Salut».', en: 'See how your body has recovered under “Health”.' },
  'tour.maintaining.progress.title': { es: 'Logros', ca: 'Assoliments', en: 'Achievements' },
  'tour.maintaining.progress.body': { es: 'Tus niveles y logros te esperan en «Progreso».', ca: 'Els teus nivells i assoliments t’esperen a «Progrés».', en: 'Your levels and achievements are waiting under “Progress”.' },
  'share.achievement': {
    es: '🏆 He desbloqueado «{title}» dejando de fumar con Respiria. 💪\nrespiria.isaies.com',
    ca: '🏆 He desbloquejat «{title}» deixant de fumar amb Respiria. 💪\nrespiria.isaies.com',
    en: '🏆 I unlocked “{title}” quitting smoking with Respiria. 💪\nrespiria.isaies.com',
  },

  // Home
  'home.greeting': { es: 'Hola', ca: 'Hola', en: 'Hi' },
  'home.greetingName': { es: 'Hola, {name}', ca: 'Hola, {name}', en: 'Hi, {name}' },
  'home.notSmoking': { es: 'Sin fumar', ca: 'Sense fumar', en: 'Smoke-free' },
  'home.bestStreak': { es: 'Mejor racha:', ca: 'Millor ratxa:', en: 'Best streak:' },
  'home.cleanDays': { es: 'Días limpios:', ca: 'Dies nets:', en: 'Clean days:' },
  'home.quitDay': { es: 'Tu Día D', ca: 'El teu Dia D', en: 'Your Quit Day' },
  // Sin fecha todavía: el nombre del hito, dentro de una frase ya traducida.
  'common.quitDay': { es: 'Día D', ca: 'Dia D', en: 'Quit Day' },
  'home.goingDown': { es: 'Vas bajando', ca: 'Vas baixant', en: "You're cutting down" },
  'home.cigsToday': { es: 'cigarros hoy', ca: 'cigarrets avui', en: 'cigarettes today' },
  'home.yesterday': { es: 'Ayer:', ca: 'Ahir:', en: 'Yesterday:' },
  'home.before': { es: 'Antes:', ca: 'Abans:', en: 'Before:' },
  'home.perDay': { es: '/día', ca: '/dia', en: '/day' },
  'home.avoided': { es: 'Cigarros evitados', ca: 'Cigarrets evitats', en: 'Cigarettes avoided' },
  'home.saved': { es: 'Ahorrado', ca: 'Estalviat', en: 'Saved' },
  'home.cravingsBeaten': { es: 'Antojos superados', ca: 'Ganes superades', en: 'Cravings beaten' },
  'home.recovery': {
    es: 'Tu recuperación',
    ca: 'La teva recuperació',
    'ca-valencia': 'La teua recuperació',
    en: 'Your recovery',
  },
  'home.smoker.title': { es: '¿Listo para dejarlo?', ca: 'Preparat per deixar-ho?', en: 'Ready to quit?' },
  'home.smoker.body': {
    es: 'Marca el día en que quieres dejar de fumar. Desde ahí te acompañamos: cuenta atrás, ánimos y tu progreso.',
    ca: 'Marca el dia en què vols deixar de fumar. Des d’aquí t’acompanyem: compte enrere, ànims i el teu progrés.',
    en: 'Pick the day you want to quit. From there we’re with you: a countdown, encouragement and your progress.',
  },
  'home.smoker.today': { es: 'Hoy', ca: 'Avui', en: 'Today' },
  'home.smoker.week': { es: 'En 1 sem.', ca: 'En 1 set.', en: 'In 1 wk' },
  'home.smoker.twoWeeks': { es: 'En 2 sem.', ca: 'En 2 set.', en: 'In 2 wks' },
  'home.smoker.month': { es: 'En 1 mes', ca: 'En 1 mes', en: 'In 1 mo' },
  'home.smoker.cta': { es: 'Marcar mi fecha', ca: 'Marcar la meva data', en: 'Set my date' },
  'home.smoker.smokedToday': { es: 'cigarros hoy', ca: 'cigarrets avui', en: 'cigarettes today' },
  'home.smoker.logCig': { es: 'Registrar cigarro', ca: 'Registrar cigarret', en: 'Log a cigarette' },
  'home.smoker.baseline': { es: 'Tu media: {n}/día', ca: 'La teva mitjana: {n}/dia', en: 'Your average: {n}/day' },
  'home.smoker.goalLabel': { es: 'Objetivo de hoy', ca: 'Objectiu d’avui', en: 'Today’s goal' },
  'home.smoker.goalOk': { es: 'Vas bien: máx. {n} hoy 👌', ca: 'Vas bé: màx. {n} avui 👌', en: 'On track: max {n} today 👌' },
  'home.smoker.goalOver': { es: 'Hoy llevas {n} de {target}', ca: 'Avui portes {n} de {target}', en: 'Today you’re at {n} of {target}' },
  'home.smoker.goalSet': { es: 'Objetivo: {n}/día', ca: 'Objectiu: {n}/dia', en: 'Goal: {n}/day' },
  'home.smoker.goalSave': { es: 'Fijar objetivo', ca: 'Fixar objectiu', en: 'Set goal' },
  'home.smoker.goalPrompt': { es: 'Ponte un tope diario para ir bajando', ca: 'Posa’t un límit diari per anar baixant', en: 'Set a daily cap to bring it down' },
  'home.smoker.change': { es: 'Cambiar', ca: 'Canviar', en: 'Change' },
  'home.smoker.whyTitle': { es: 'Lo que ganas al dejarlo', ca: 'El que guanyes en deixar-ho', en: 'What you gain by quitting' },
  'home.smoker.why1': {
    es: 'A los 20 minutos, tu pulso y tu tensión empiezan a bajar.',
    ca: 'Als 20 minuts, el teu pols i la teva tensió comencen a baixar.',
    en: 'After 20 minutes, your pulse and blood pressure start to drop.',
  },
  'home.smoker.why2': {
    es: 'En unas semanas respiras mejor y te cuesta menos moverte.',
    ca: 'En unes setmanes respires millor i et costa menys moure’t.',
    en: 'In a few weeks you breathe better and moving feels easier.',
  },
  'home.smoker.why3': {
    es: 'Al año sin fumar, tu riesgo de infarto baja casi a la mitad.',
    ca: 'A l’any sense fumar, el teu risc d’infart baixa quasi a la meitat.',
    en: 'After a year smoke-free, your heart-attack risk drops by nearly half.',
  },
  'home.smoker.measure': {
    es: 'Mídete la tensión hoy en el check-in: así verás el cambio más adelante.',
    ca: 'Mesura’t la tensió avui al check-in: així veuràs el canvi més endavant.',
    en: 'Log your blood pressure in today’s check-in — you’ll see the change later.',
  },
  'home.level': { es: 'Nivel {n}', ca: 'Nivell {n}', en: 'Level {n}' },
  'home.money': { es: 'Tu dinero', ca: 'Els teus diners', en: 'Your money' },
  'home.savedAmount': { es: '{money} ahorrados', ca: '{money} estalviats', en: '{money} saved' },

  // Treatment (home card)
  'treatment.remaining': {
    es: '{n} comprimidos restantes',
    ca: '{n} comprimits restants',
    en: '{n} pills left',
  },
  'treatment.nextDose': { es: 'Próxima toma {when}', ca: 'Pròxima presa {when}', en: 'Next dose {when}' },
  'treatment.taken': {
    es: 'Tomada la de las {time}',
    ca: 'Presa de les {time} feta',
    en: 'Took the {time} one',
  },

  // Treatment (screen)
  'treatment.title': { es: 'Tratamiento', ca: 'Tractament', en: 'Treatment' },

  // Sin pauta: cómo conseguir las pastillas y empezarla (#95)
  'startTreatment.title': {
    es: 'Quiero tomar las pastillas',
    ca: 'Vull prendre les pastilles',
    en: 'I want to take the pills',
  },
  'startTreatment.intro': {
    es: 'Las pastillas de citisina (Recigarum, Todacitan) las receta tu médico y las cubre la Seguridad Social. Así se consiguen:',
    ca: 'Les pastilles de citisina (Recigarum, Todacitan) les recepta el teu metge i les cobreix la Seguretat Social. Així s’aconsegueixen:',
    en: 'Cytisine pills (Recigarum, Todacitan) are prescribed by your doctor and covered by the public health system in Spain. Here’s how to get them:',
  },
  'startTreatment.step1Title': {
    es: 'Pide cita en enfermería familiar',
    ca: 'Demana cita a infermeria familiar',
    en: 'Book an appointment with your family nurse',
  },
  'startTreatment.step1Body': {
    es: 'En tu centro de salud. Te harán un test: unas preguntas sobre cuánto fumas y si de verdad estás decidido a dejarlo.',
    ca: 'Al teu centre de salut. Et faran un test: unes preguntes sobre quant fumes i si de veritat estàs decidit a deixar-ho.',
    en: 'At your health centre. They’ll run a short test: a few questions about how much you smoke and whether you’re really set on quitting.',
  },
  'startTreatment.step2Title': {
    es: 'Pide cita con tu médico de cabecera',
    ca: 'Demana cita amb el teu metge de capçalera',
    en: 'Book an appointment with your GP',
  },
  'startTreatment.step2Body': {
    es: 'Es quien te receta las pastillas, con el resultado del test de enfermería.',
    ca: 'És qui et recepta les pastilles, amb el resultat del test d’infermeria.',
    en: 'Your GP is the one who prescribes the pills, using the result of the nurse’s test.',
  },
  'startTreatment.step3Title': {
    es: 'Cuando tengas la caja, vuelve aquí',
    ca: 'Quan tinguis la capsa, torna aquí',
    en: 'When you have the box, come back here',
  },
  'startTreatment.step3Body': {
    es: 'Pulsa «Ya tengo las pastillas» y la app te organiza las 100 tomas de los 25 días.',
    ca: 'Prem «Ja tinc les pastilles» i l’app t’organitza les 100 preses dels 25 dies.',
    en: 'Tap “I have the pills” and the app lays out the 100 doses over the 25 days.',
  },
  'startTreatment.tipTitle': { es: 'Un consejo', ca: 'Un consell', en: 'A tip' },
  'startTreatment.tipBody': {
    es: 'Pide las dos citas a la vez, para el mismo día o con pocos días de diferencia. Así no se te enfría la decisión entre una y otra.',
    ca: 'Demana les dues cites alhora, per al mateix dia o amb pocs dies de diferència. Així no se’t refreda la decisió entre l’una i l’altra.',
    en: 'Book both appointments at once, for the same day or a few days apart. That way your resolve doesn’t cool off between them.',
  },
  'startTreatment.havePills': {
    es: 'Ya tengo las pastillas',
    ca: 'Ja tinc les pastilles',
    en: 'I have the pills',
  },
  'startTreatment.back': { es: 'Volver a la guía', ca: 'Tornar a la guia', en: 'Back to the guide' },
  'startTreatment.startButton': {
    es: 'Empezar la pauta',
    ca: 'Començar la pauta',
    en: 'Start the treatment',
  },
  'startTreatment.error': {
    es: 'No se ha podido crear la pauta. Inténtalo de nuevo.',
    ca: 'No s’ha pogut crear la pauta. Torna-ho a provar.',
    en: 'The treatment could not be created. Please try again.',
  },
  'treatment.progressAria': {
    es: 'Tratamiento al {n} por ciento',
    ca: 'Tractament al {n} per cent',
    en: 'Treatment {n} percent',
  },
  'treatment.dayOf': { es: 'de {n}', ca: 'de {n}', en: 'of {n}' },
  'treatment.completed': { es: 'Completado', ca: 'Completat', en: 'Completed' },
  'treatment.takenCount': { es: 'Tomados', ca: 'Preses', en: 'Taken' },
  'treatment.remainingLabel': { es: 'Restantes', ca: 'Restants', en: 'Remaining' },
  'treatment.adherence': { es: 'Adherencia', ca: 'Adherència', en: 'Adherence' },
  'treatment.finish': { es: 'Terminas', ca: 'Acabes', en: 'You finish' },
  'treatment.takenValue': { es: '{taken} de {total}', ca: '{taken} de {total}', en: '{taken} of {total}' },
  'treatment.blisterHint': {
    es: 'A partir del día 21 el prospecto solo dice «1-2 comprimidos al día», sin fijar horas. Las que ves las ha repartido la app, no el prospecto. Tu blíster viene calendarizado: échale un ojo y ajústalas si hace falta.',
    ca: 'A partir del dia 21 el prospecte només diu «1-2 comprimits al dia», sense fixar hores. Les que veus les ha repartit l’app, no el prospecte. El teu blíster ve calendaritzat: fes-hi un cop d’ull i ajusta-les si cal.',
    en: 'From day 21 the leaflet only says “1-2 pills a day”, without set times. The app spread these out, not the leaflet. Your blister is calendared: check it and adjust them if needed.',
  },
  'treatment.prevDay': { es: 'Día anterior', ca: 'Dia anterior', en: 'Previous day' },
  'treatment.nextDay': { es: 'Día siguiente', ca: 'Dia següent', en: 'Next day' },
  'treatment.dayN': { es: 'Día {n}', ca: 'Dia {n}', en: 'Day {n}' },
  'treatment.todaySuffix': { es: ' · hoy', ca: ' · avui', en: ' · today' },
  'treatment.backToToday': { es: 'Volver a hoy', ca: 'Tornar a avui', en: 'Back to today' },
  'treatment.finishedTitle': {
    es: 'Tratamiento completado',
    ca: 'Tractament completat',
    en: 'Treatment completed',
  },
  'treatment.finishedBody': {
    es: 'Terminaste las {n} tomas el {date}. Puedes seguir consultando los días y marcar lo que se quedó pendiente.',
    ca: 'Vas acabar les {n} preses el {date}. Pots seguir consultant els dies i marcar el que va quedar pendent.',
    en: 'You finished all {n} days on {date}. You can still look back and mark anything left pending.',
  },
  'treatment.sinceLastPill': {
    es: '{n} días desde la última pastilla',
    ca: '{n} dies des de l’última pastilla',
    en: '{n} days since the last pill',
  },
  'treatment.sinceLastPillToday': {
    es: 'Hoy ha sido la última pastilla',
    ca: 'Avui ha estat l’última pastilla',
    en: 'Today was the last pill',
  },
  'treatment.nextStageTitle': {
    es: 'La caja se acaba, el proceso no',
    ca: 'La caixa s’acaba, el procés no',
    en: 'The box ends, the process does not',
  },
  'treatment.nextStageBody': {
    es: 'Terminar las pastillas no es terminar de dejarlo. Tu racha, tu ahorro y tu salud siguen contando desde tu Día D, igual que ayer.',
    ca: 'Acabar les pastilles no és acabar de deixar-ho. La teva ratxa, el teu estalvi i la teva salut segueixen comptant des del teu Dia D, igual que ahir.',
    en: 'Finishing the pills is not finishing quitting. Your streak, your savings and your health keep counting from your Quit Day, just like yesterday.',
  },
  'situations.title': { es: 'Tus situaciones', ca: 'Les teves situacions', en: 'Your situations' },
  'situations.body': {
    es: 'Cada vez que pasas por una de estas sin fumar, esa asociación se debilita. Es así como se apagan: una a una, no con el calendario.',
    ca: 'Cada vegada que passes per una d’aquestes sense fumar, aquesta associació es debilita. És així com s’apaguen: una a una, no amb el calendari.',
    en: 'Every time you go through one of these without smoking, that link gets weaker. That is how they fade: one at a time, not with the calendar.',
  },
  'situations.mastered': { es: 'Desactivada', ca: 'Desactivada', en: 'Defused' },
  'situations.wins': { es: '{n} de {total}', ca: '{n} de {total}', en: '{n} of {total}' },
  'situations.withUrge': { es: 'Me apetecía', ca: 'Em venia de gust', en: 'I wanted one' },
  'situations.without': { es: 'Ni me apeteció', ca: 'Ni em va venir de gust', en: "Didn't even want one" },
  'stage.homeSituations': {
    es: '{n} de {total} situaciones desactivadas',
    ca: '{n} de {total} situacions desactivades',
    en: '{n} of {total} situations defused',
  },
  'stage.homeWithoutPills': {
    es: '{n} días sin la pastilla',
    ca: '{n} dies sense la pastilla',
    en: '{n} days without the pill',
  },
  'stage.pageTitle': { es: 'Tu etapa', ca: 'La teva etapa', en: 'Your stage' },
  'stage.none': {
    es: 'Esta sección aparece cuando tienes un Día D. Ahí empieza lo que se cuenta aquí.',
    ca: 'Aquesta secció apareix quan tens un Dia D. Aquí comença el que es compta aquí.',
    en: 'This section shows up once you have a Quit Day. That is where all this starts counting.',
  },
  'stage.goToStage': { es: 'Ver tu etapa y tus situaciones', ca: 'Veure la teva etapa i les teves situacions', en: 'See your stage and your situations' },
  'stage.withoutPillsTitle': { es: 'Sin la pastilla', ca: 'Sense la pastilla', en: 'Without the pill' },
  'stage.unsupported.fresh': {
    es: 'Llevas {n} días sin el apoyo de la pastilla, y es el tramo que más cuidado pide. Pero no vuelves al principio: los {quit} días sin fumar que llevas siguen contando, y la parte física ya la tienes andada.',
    ca: 'Fa {n} dies que no tens el suport de la pastilla, i és el tram que demana més cura. Però no tornes al principi: els {quit} dies sense fumar que portes segueixen comptant, i la part física ja la tens feta.',
    en: "It's been {n} days without the pill's support, and this is the stretch that asks for most care. But you are not back at the start: your {quit} days smoke-free still count, and the physical part is already behind you.",
  },
  'stage.unsupported.settling': {
    es: '{n} días sin la pastilla. La ayuda ya no está y tú sigues aquí: eso es tuyo, no del tratamiento.',
    ca: '{n} dies sense la pastilla. L’ajuda ja no hi és i tu segueixes aquí: això és teu, no del tractament.',
    en: '{n} days without the pill. The help is gone and you are still here: that one is yours, not the treatment’s.',
  },
  'stage.unsupported.past': {
    es: 'Más de dos meses sin la pastilla. A estas alturas lo que sostiene esto eres tú.',
    ca: 'Més de dos mesos sense la pastilla. A hores d’ara qui sosté això ets tu.',
    en: 'More than two months without the pill. By now the one holding this up is you.',
  },
  'stage.title': { es: 'Cómo suele ir esto', ca: 'Com sol anar això', en: 'How this usually goes' },
  // La frase anterior («no una medida de ti») no se entendía: no decía de qué
  // hablaba ni para qué servía. Ahora dice lo que NO es y lo que SÍ es, y la
  // fuente se separa como nota al pie.
  'stage.caption': {
    es: 'Esto no mide cómo vas tú: es el recorrido que suele seguir la gente al dejarlo, para que sepas qué esperar.',
    ca: 'Això no mesura com vas tu: és el recorregut que sol seguir la gent quan ho deixa, perquè sàpigues què esperar.',
    en: 'This isn’t measuring how you’re doing: it’s the path most people follow when they quit, so you know what to expect.',
  },
  'stage.physicalLabel': { es: 'Dependencia física', ca: 'Dependència física', en: 'Physical dependence' },
  'stage.conditionedLabel': { es: 'Ganas por situación', ca: 'Ganes per situació', en: 'Situation-driven urges' },
  'stage.physical.peak': {
    es: 'Ahora es lo más fuerte. Suele concentrarse en los 3 primeros días.',
    ca: 'Ara és el més fort. Sol concentrar-se en els 3 primers dies.',
    en: 'This is the strongest it gets. It tends to peak in the first 3 days.',
  },
  'stage.physical.strong': {
    es: 'Sigue fuerte, pero ya empieza a ceder.',
    ca: 'Encara és fort, però ja comença a cedir.',
    en: "Still strong, but it's starting to give.",
  },
  'stage.physical.easing': {
    es: 'Va bajando. En la mayoría se calma a lo largo del primer mes.',
    ca: 'Va baixant. En la majoria es calma al llarg del primer mes.',
    en: 'Easing off. For most people it settles over the first month.',
  },
  'stage.physical.mostlyGone': {
    es: 'Para la mayoría ya ha pasado. A algunas personas les dura más, y también es normal.',
    ca: 'Per a la majoria ja ha passat. A algunes persones els dura més, i també és normal.',
    en: "For most people it's over. For some it lasts longer, and that's normal too.",
  },
  'stage.conditioned.everywhere': {
    es: 'Casi cualquier momento del día sigue asociado al tabaco.',
    ca: 'Gairebé qualsevol moment del dia continua associat al tabac.',
    en: 'Almost any moment of the day is still linked to smoking.',
  },
  'stage.conditioned.situational': {
    es: 'Ya no es constante: aparece en situaciones concretas. Estas no se van solas con el tiempo — se apagan situación a situación.',
    ca: 'Ja no és constant: apareix en situacions concretes. Aquestes no se’n van soles amb el temps — s’apaguen situació a situació.',
    en: "No longer constant: it shows up in specific situations. These don't fade on their own with time — they go out one situation at a time.",
  },
  'stage.conditioned.spaced': {
    es: 'Cada vez más espaciadas. Puede aparecer alguna suelta, y no significa que hayas retrocedido.',
    ca: 'Cada vegada més espaiades. Pot aparèixer alguna de solta, i no vol dir que hagis retrocedit.',
    en: "Further and further apart. The odd one may still show up, and it doesn't mean you've gone backwards.",
  },
  'stage.source': {
    es: 'A partir de información pública del NHS y el NCI.',
    ca: 'A partir d’informació pública del NHS i el NCI.',
    en: 'Based on public information from the NHS and the NCI.',
  },
  'maintenance.offerTitle': {
    es: '¿Pasamos a mantenimiento?',
    ca: 'Passem a manteniment?',
    en: 'Shall we switch to maintenance?',
  },
  'maintenance.offerBody': {
    es: 'Hace {n} días de tu última pastilla. Si ya no lo vives como «dejándolo», la app puede acompañarte en modo mantenimiento. No pierdes nada: tu racha, tu ahorro y tus logros siguen exactamente donde están.',
    ca: 'Fa {n} dies de l’última pastilla. Si ja no ho vius com «deixant-ho», l’app pot acompanyar-te en mode manteniment. No perds res: la teva ratxa, el teu estalvi i els teus assoliments es queden exactament on són.',
    en: "It's been {n} days since your last pill. If this no longer feels like quitting, the app can switch to maintenance mode. Nothing is lost: your streak, your savings and your achievements stay exactly where they are.",
  },
  'maintenance.accept': { es: 'Sí, mantenimiento', ca: 'Sí, manteniment', en: 'Yes, maintenance' },
  'maintenance.later': { es: 'Aún no', ca: 'Encara no', en: 'Not yet' },
  'maintenance.laterHint': {
    es: 'Si dices que aún no, la app no vuelve a preguntar en un mes.',
    ca: 'Si dius que encara no, l’app no torna a preguntar en un mes.',
    en: "If you say not yet, the app won't ask again for a month.",
  },
  'more.treatmentDone': {
    es: 'Pauta completada',
    ca: 'Pauta completada',
    en: 'Course completed',
  },
  'treatment.lastDaySuffix': { es: ' · último día', ca: ' · últim dia', en: ' · last day' },
  'treatment.noDoses': {
    es: 'Este día no tiene tomas programadas.',
    ca: 'Aquest dia no té preses programades.',
    en: 'No doses scheduled for this day.',
  },
  'treatment.disclaimer1': {
    es: 'Respiria organiza tu tratamiento, pero no sustituye a tu médico ni al prospecto. Si te saltas una toma, sigue con la siguiente: ',
    ca: 'Respiria organitza el teu tractament, però no substitueix el teu metge ni el prospecte. Si et saltes una presa, segueix amb la següent: ',
    en: "Respiria organises your treatment, but it doesn't replace your doctor or the leaflet. If you miss a dose, carry on with the next one: ",
  },
  'treatment.disclaimerStrong': {
    es: 'no tomes una dosis doble para compensar',
    ca: 'no prenguis una dosi doble per compensar',
    'ca-valencia': 'no prengues una dosi doble per compensar',
    en: "don't take a double dose to make up for it",
  },
  'treatment.statusTaken': { es: 'Tomada', ca: 'Presa', en: 'Taken' },
  'treatment.statusSkipped': { es: 'Saltada', ca: 'Saltada', en: 'Skipped' },
  'treatment.statusMissed': { es: 'Sin marcar', ca: 'Sense marcar', en: 'Unmarked' },
  'treatment.editTimeAria': {
    es: 'Cambiar la hora de esta toma',
    ca: 'Canviar l’hora d’aquesta presa',
    en: "Change this dose's time",
  },
  'treatment.editTimeAtAria': {
    es: 'Cambiar la hora de la toma de las {time}',
    ca: 'Canviar l’hora de la presa de les {time}',
    en: 'Change the {time} dose time',
  },
  'treatment.undoAria': {
    es: 'Deshacer la toma de las {time}',
    ca: 'Desfer la presa de les {time}',
    en: 'Undo the {time} dose',
  },
  'treatment.undo': { es: 'Deshacer', ca: 'Desfer', en: 'Undo' },
  'treatment.notYet': { es: 'aún no toca', ca: 'encara no toca', en: 'not yet' },
  'treatment.skipAria': { es: 'Saltar esta toma', ca: 'Saltar aquesta presa', en: 'Skip this dose' },
  'treatment.markTaken': { es: 'Tomada', ca: 'Presa', en: 'Take' },

  // Craving flow
  'craving.close': { es: 'Cerrar', ca: 'Tancar', en: 'Close' },
  'craving.howMuch': { es: '¿Cuántas ganas tienes?', ca: 'Quantes ganes tens?', en: 'How strong is the craving?' },
  'craving.skipHint': {
    es: 'Puedes saltártelo y empezar directamente.',
    ca: 'Pots saltar-t’ho i començar directament.',
    en: 'You can skip this and start right away.',
  },
  'craving.intensityAria': { es: 'Intensidad del antojo', ca: 'Intensitat de les ganes', en: 'Craving intensity' },
  'craving.trigger': { es: '¿Qué lo ha disparado?', ca: 'Què ho ha provocat?', en: 'What set it off?' },
  'craving.start': { es: 'Empezar', ca: 'Començar', en: 'Start' },
  // Guided breathing (BreathingCircle) — used in the craving flow and in
  // emergency mode. Wording kept in step with `craving.wave3`, which names the
  // same three phases in a sentence.
  'breathing.in': { es: 'Inhala', ca: 'Inspira', en: 'Breathe in' },
  'breathing.hold': { es: 'Mantén', ca: 'Aguanta', en: 'Hold' },
  'breathing.out': { es: 'Suelta', ca: 'Deixa anar', en: 'Breathe out' },

  'craving.wave1': {
    es: 'Casi todas las ganas desaparecen en pocos minutos. Esta también.',
    ca: 'Gairebé totes les ganes desapareixen en pocs minuts. Aquesta també.',
    en: 'Almost every craving fades in a few minutes. This one too.',
  },
  'craving.wave2': {
    es: 'No tienes que hacer nada. Solo esperar.',
    ca: 'No has de fer res. Només esperar.',
    en: "You don't have to do anything. Just wait.",
  },
  'craving.wave3': {
    es: 'Respira con el círculo. Inhala, mantén, suelta.',
    ca: 'Respira amb el cercle. Inspira, aguanta, deixa anar.',
    en: 'Breathe with the circle. In, hold, out.',
  },
  'craving.wave4': {
    es: 'Esto ya lo has pasado antes.',
    ca: 'Això ja ho has passat abans.',
    en: "You've been through this before.",
  },
  'craving.wave5': {
    es: 'Cada segundo que aguantas, el antojo pierde fuerza.',
    ca: 'Cada segon que aguantes, les ganes perden força.',
    en: 'Every second you hold on, the craving loses strength.',
  },
  'craving.betterNow': { es: 'Ya estoy mejor', ca: 'Ja estic millor', en: "I'm better now" },
  'craving.oneMoreMin': { es: 'Necesito un minuto más', ca: 'Necessito un minut més', en: 'I need one more minute' },
  'craving.yourMission': {
    es: 'Tu misión',
    ca: 'La teva missió',
    'ca-valencia': 'La teua missió',
    en: 'Your mission',
  },
  'craving.missionFallback': {
    es: 'Bébete un vaso de agua entero',
    ca: 'Beu-te un got d’aigua sencer',
    en: 'Drink a full glass of water',
  },
  'craving.aboutMin': { es: 'Unos {n} min', ca: 'Uns {n} min', en: 'About {n} min' },
  'craving.done': { es: 'Hecho', ca: 'Fet', en: 'Done' },
  'craving.otherMission': { es: 'Otra misión', ca: 'Una altra missió', en: 'Another mission' },
  'craving.notNow': { es: 'Ahora no', ca: 'Ara no', en: 'Not now' },
  'craving.heldStrongest': { es: 'Has aguantado lo más fuerte', ca: 'Has aguantat el més fort', en: 'You got through the worst' },
  'craving.partialText': {
    es: 'Que quede algo de ganas es normal. Suele irse del todo con unos minutos más.',
    ca: 'Que quedin unes poques ganes és normal. Sol marxar del tot amb uns minuts més.',
    en: 'Some craving left is normal. It usually goes with a few more minutes.',
  },
  'craving.holdCoupleMin': { es: 'Aguanto un par de minutos más', ca: 'Aguanto un parell de minuts més', en: "I'll hold on a couple more minutes" },
  'craving.fineClose': { es: 'Ya estoy bien, cerrar', ca: 'Ja estic bé, tancar', en: "I'm fine, close" },
  'craving.noProblem': { es: 'Sin problema', ca: 'Cap problema', en: 'No problem' },
  'craving.smokedText': {
    es: 'Un cigarro es un tropiezo, no el final del camino. Todo lo que llevas ahorrado y todos los cigarros que no te has fumado siguen ahí.',
    ca: 'Un cigarret és un ensopec, no el final del camí. Tot el que portes estalviat i tots els cigarrets que no t’has fumat segueixen aquí.',
    en: "One cigarette is a stumble, not the end of the road. Everything you've saved and every cigarette you haven't smoked are still there.",
  },
  'craving.wait5': { es: 'Espero 5 minutos antes', ca: 'Espero 5 minuts abans', en: "I'll wait 5 minutes first" },
  'craving.logSmoked': { es: 'Registrar que he fumado', ca: 'Registrar que he fumat', en: 'Log that I smoked' },
  'craving.stillWant': { es: '¿Sigues queriendo fumar?', ca: 'Encara vols fumar?', en: 'Do you still want to smoke?' },
  'craving.resisted': { es: 'No, ya se ha pasado', ca: 'No, ja se m’ha passat', en: "No, it's passed" },
  'craving.aLittle': { es: 'Un poco', ca: 'Una mica', en: 'A little' },
  'craving.smokedYes': { es: 'Sí, he fumado', ca: 'Sí, he fumat', en: 'Yes, I smoked' },

  // Notifications (Avisos)
  'notif.title': { es: 'Avisos', ca: 'Avisos', en: 'Notifications' },
  'notif.doses.label': { es: 'Tomas del tratamiento', ca: 'Preses del tractament', en: 'Treatment doses' },
  'notif.doses.desc': {
    es: 'A la hora de cada pastilla, y un recordatorio si se pasa.',
    ca: 'A l’hora de cada pastilla, i un recordatori si es passa.',
    en: "At each pill time, and a reminder if it's missed.",
  },
  'notif.checkin.label': { es: 'Check-in diario', ca: 'Check-in diari', en: 'Daily check-in' },
  'notif.checkin.desc': {
    es: 'A la hora que elijas, solo si no lo has hecho.',
    ca: 'A l’hora que triïs, només si no l’has fet.',
    en: "At the time you choose, only if you haven't done it.",
  },
  'notif.milestones.label': { es: 'Hitos y logros', ca: 'Fites i assoliments', en: 'Milestones and achievements' },
  'notif.milestones.desc': {
    es: 'Cuando tu cuerpo alcanza un hito o desbloqueas un logro.',
    ca: 'Quan el teu cos assoleix una fita o desbloqueges un assoliment.',
    en: 'When your body reaches a milestone or you unlock an achievement.',
  },
  'notif.encouragement.label': { es: 'Ánimos', ca: 'Ànims', en: 'Encouragement' },
  'notif.encouragement.desc': {
    es: 'Un recordatorio de lo que llevas ganado, por la tarde y como mucho un día sí y otro no.',
    ca: 'Un recordatori del que portes guanyat, a la tarda i com a molt un dia sí i un altre no.',
    en: "A reminder of what you've gained, in the afternoon and at most every other day.",
  },
  'notif.unsupported': {
    es: 'Este navegador no admite avisos. En Android funcionan en Chrome; en iPhone hay que instalar la app en la pantalla de inicio primero.',
    ca: 'Aquest navegador no admet avisos. A Android funcionen a Chrome; a l’iPhone cal instal·lar l’app a la pantalla d’inici primer.',
    en: 'This browser does not support notifications. On Android they work in Chrome; on iPhone you must install the app to the home screen first.',
  },
  'notif.unconfigured': {
    es: 'Los avisos no están configurados en el servidor.',
    ca: 'Els avisos no estan configurats al servidor.',
    en: 'Notifications are not configured on the server.',
  },
  'notif.denied1': {
    es: 'Los avisos están bloqueados para esta web.',
    ca: 'Els avisos estan bloquejats per a aquest web.',
    en: 'Notifications are blocked for this site.',
  },
  'notif.denied2': {
    es: 'Para permitirlos: candado o icono junto a la dirección → Permisos → Notificaciones → Permitir. Luego vuelve aquí.',
    ca: 'Per permetre’ls: cadenat o icona al costat de l’adreça → Permisos → Notificacions → Permet. Després torna aquí.',
    en: 'To allow them: padlock or icon next to the address → Permissions → Notifications → Allow. Then come back here.',
  },
  'notif.noneTitle': { es: 'Sin avisos en este dispositivo', ca: 'Sense avisos en aquest dispositiu', en: 'No notifications on this device' },
  'notif.noneBody': {
    es: 'Te avisaré a la hora de cada pastilla, del check-in y cuando alcances un hito. Nada más, y nunca en tus horas de silencio.',
    ca: 'T’avisaré a l’hora de cada pastilla, del check-in i quan assoleixis una fita. Res més, i mai a les teves hores de silenci.',
    en: "I'll remind you at each pill time, for the check-in and when you reach a milestone. Nothing else, and never during your quiet hours.",
  },
  'notif.enable': { es: 'Activar los avisos', ca: 'Activar els avisos', en: 'Turn on notifications' },
  'notif.enabling': { es: 'Activando…', ca: 'Activant…', en: 'Turning on…' },
  'notif.activeTitle': { es: 'Avisos activos', ca: 'Avisos actius', en: 'Notifications on' },
  'notif.oneDevice': { es: 'Un dispositivo registrado.', ca: 'Un dispositiu registrat.', en: 'One device registered.' },
  'notif.nDevices': { es: '{n} dispositivos registrados.', ca: '{n} dispositius registrats.', en: '{n} devices registered.' },
  'notif.sendTest': { es: 'Mandarme una de prueba', ca: 'Enviar-me’n una de prova', en: 'Send me a test one' },
  'notif.sending': { es: 'Enviando…', ca: 'Enviant…', en: 'Sending…' },
  'notif.disable': { es: 'Desactivar en este dispositivo', ca: 'Desactivar en aquest dispositiu', en: 'Turn off on this device' },
  'notif.msgEnabled': { es: 'Listo, este dispositivo ya recibe avisos.', ca: 'Fet, aquest dispositiu ja rep avisos.', en: 'Done, this device now receives notifications.' },
  'notif.msgDisabled': { es: 'Este dispositivo ya no recibirá avisos.', ca: 'Aquest dispositiu ja no rebrà avisos.', en: 'This device will no longer receive notifications.' },
  'notif.msgSent': { es: 'Enviada. Debería aparecerte en un momento.', ca: 'Enviada. Hauria d’aparèixer d’un moment a l’altre.', en: 'Sent. It should show up in a moment.' },
  'notif.msgNotSent': { es: 'No ha llegado a ningún dispositivo.', ca: 'No ha arribat a cap dispositiu.', en: "It didn't reach any device." },
  'notif.msgError': { es: 'No he podido enviarla. ¿Sigue activo este dispositivo?', ca: 'No l’he pogut enviar. Encara està actiu aquest dispositiu?', en: 'I couldn’t send it. Is this device still active?' },
  'notif.whatToNotify': { es: 'Qué quieres que te avise', ca: 'Què vols que t’avisi', en: 'What to notify you about' },
  'notif.quietHours': { es: 'Horas de silencio', ca: 'Hores de silenci', en: 'Quiet hours' },
  'notif.quietHoursDesc': {
    es: 'Entre estas horas no te llegará nada, ni siquiera los avisos de las pastillas.',
    ca: 'Entre aquestes hores no t’arribarà res, ni tan sols els avisos de les pastilles.',
    en: "Nothing reaches you between these hours, not even pill reminders.",
  },
  'notif.from': { es: 'Desde', ca: 'Des de', en: 'From' },
  'notif.to': { es: 'Hasta', ca: 'Fins a', en: 'To' },
  'notif.checkinTime': { es: 'Check-in', ca: 'Check-in', en: 'Check-in' },
  'notif.rulesFooter': {
    es: 'Como mucho un aviso por hora y seis al día, y nunca dos seguidas de lo mismo. Los de las pastillas se saltan el límite por hora, porque son tu pauta. Si dejas de abrir un tipo de aviso, bajo su frecuencia solo.',
    ca: 'Com a molt un avís per hora i sis al dia, i mai dos seguits del mateix. Els de les pastilles se salten el límit per hora, perquè són la teva pauta. Si deixes d’obrir un tipus d’avís, en baixo la freqüència sol.',
    en: 'At most one notification per hour and six a day, and never two of the same in a row. Pill reminders skip the hourly limit, since they’re your schedule. If you stop opening a type, I ease off it on my own.',
  },

  // Settings (Ajustes)
  'settings.title': { es: 'Ajustes', ca: 'Ajustos', en: 'Settings' },
  'settings.quitDay.title': { es: 'Tu Día D', ca: 'El teu Dia D', en: 'Your Quit Day' },
  'settings.quitDay.desc': {
    es: 'El día que dejaste (o dejas) de fumar. Desde aquí cuentan la racha, el ahorro y la recuperación. Cambiarlo no borra nada de lo registrado.',
    ca: 'El dia que vas deixar (o deixes) de fumar. Des d’aquí compten la ratxa, l’estalvi i la recuperació. Canviar-ho no esborra res del registrat.',
    en: 'The day you quit (or quit). Your streak, savings and recovery count from here. Changing it deletes nothing you logged.',
  },
  'settings.med.title': { es: 'Tu medicamento', ca: 'El teu medicament', en: 'Your medication' },
  'settings.med.desc': {
    es: 'El nombre que se muestra en la pantalla de tratamiento.',
    ca: 'El nom que es mostra a la pantalla de tractament.',
    en: 'The name shown on the treatment screen.',
  },
  'settings.tobacco.title': { es: 'Tu tabaco', ca: 'El teu tabac', en: 'Your tobacco' },
  'settings.tobacco.desc': {
    es: 'Con esto se calcula lo que ahorras. Afínalo cuando sepas cuánto te dura.',
    ca: 'Amb això es calcula el que estalvies. Afina-ho quan sàpigues quant et dura.',
    en: 'This is what your savings are calculated from. Fine-tune it once you know how long it lasts.',
  },
  'settings.tobacco.locked': {
    es: 'Bloqueado para no cambiarlo sin querer. Pulsa Modificar y luego Confirmar.',
    ca: 'Bloquejat per no canviar-ho sense voler. Prem Modificar i després Confirmar.',
    en: 'Locked so you don’t change it by accident. Tap Edit, then Confirm.',
  },
  'settings.tobacco.pack': { es: 'Cajetilla', ca: 'Paquet', en: 'Pack' },
  'settings.tobacco.rolling': { es: 'De liar', ca: 'De liar', en: 'Rolling' },
  'settings.cigs': { es: 'Cigarros', ca: 'Cigarrets', en: 'Cigarettes' },
  'settings.sticks': { es: 'Sticks', ca: 'Sticks', en: 'Sticks' },
  'settings.perDay': { es: '{unit} al día', ca: '{unit} al dia', en: '{unit} a day' },
  'settings.pouchGrams': { es: 'Gramos por bolsa', ca: 'Grams per bossa', en: 'Grams per pouch' },
  'settings.pouchPrice': { es: 'Precio de la bolsa (€)', ca: 'Preu de la bossa (€)', en: 'Pouch price (€)' },
  'settings.gramsPerCig': { es: 'Gramos por cigarro', ca: 'Grams per cigarret', en: 'Grams per cigarette' },
  'settings.gramsPerCigHint': {
    es: 'Se afina con cuántos días te dura una bolsa.',
    ca: 'S’afina amb quants dies et dura una bossa.',
    en: 'Fine-tuned by how many days a pouch lasts you.',
  },
  'settings.accessories': { es: 'Papel y filtros por bolsa (€)', ca: 'Paper i filtres per bossa (€)', en: 'Papers and filters per pouch (€)' },
  'settings.accessoriesHint': {
    es: 'Lo que gastas de más en papel y filtros cada bolsa.',
    ca: 'El que gastes de més en paper i filtres cada bossa.',
    en: 'What you spend extra on papers and filters per pouch.',
  },
  'settings.perPackSticks': { es: 'Sticks por paquete', ca: 'Sticks per paquet', en: 'Sticks per pack' },
  'settings.perPackCigs': { es: 'Cigarros por cajetilla', ca: 'Cigarrets per paquet', en: 'Cigarettes per pack' },
  'settings.pricePackSticks': { es: 'Precio del paquete (€)', ca: 'Preu del paquet (€)', en: 'Pack price (€)' },
  'settings.pricePackCigs': { es: 'Precio de la cajetilla (€)', ca: 'Preu del paquet (€)', en: 'Pack price (€)' },
  'settings.appearance': { es: 'Apariencia', ca: 'Aparença', en: 'Appearance' },
  'settings.theme': { es: 'Tema', ca: 'Tema', en: 'Theme' },
  'settings.theme.system': { es: 'Sistema', ca: 'Sistema', en: 'System' },
  'settings.theme.light': { es: 'Claro', ca: 'Clar', en: 'Light' },
  'settings.theme.dark': { es: 'Oscuro', ca: 'Fosc', en: 'Dark' },
  'settings.lessMotion': { es: 'Menos animaciones', ca: 'Menys animacions', en: 'Less motion' },
  'settings.lessMotionDesc': {
    es: 'Quita el confeti y las transiciones.',
    ca: 'Treu el confeti i les transicions.',
    en: 'Removes confetti and transitions.',
  },
  'settings.notifLinkDesc': {
    es: 'Tomas, check-in, horas de silencio',
    ca: 'Preses, check-in, hores de silenci',
    en: 'Doses, check-in, quiet hours',
  },
  'settings.legalFooter': {
    es: 'Nada de lo que cambies aquí borra tu historial. Consulta el ',
    ca: 'Res del que canviïs aquí esborra el teu historial. Consulta l’',
    en: 'Nothing you change here deletes your history. See the ',
  },
  'settings.legalLink': { es: 'aviso legal y la privacidad', ca: 'avís legal i la privacitat', en: 'legal notice and privacy' },
  'settings.deleteTitle': { es: 'Borrar mi cuenta', ca: 'Esborrar el meu compte', en: 'Delete my account' },
  'settings.deleteDesc': {
    es: 'Elimina tu cuenta y todos tus datos de forma inmediata e irreversible. Si quieres guardarlos antes, expórtalos arriba.',
    ca: 'Elimina el teu compte i totes les teves dades de forma immediata i irreversible. Si els vols guardar abans, exporta’ls a dalt.',
    en: 'Deletes your account and all your data immediately and irreversibly. If you want to keep them first, export them above.',
  },
  'settings.deleting': { es: 'Borrando…', ca: 'Esborrant…', en: 'Deleting…' },
  'settings.deleteKeyword': { es: 'BORRAR', ca: 'ESBORRAR', en: 'DELETE' },
  'settings.deleteConfirm': {
    es: 'Esto borra tu cuenta y TODOS tus datos, para siempre y sin vuelta atrás.\n\nEscribe {word} para confirmar:',
    ca: 'Això esborra el teu compte i TOTES les teves dades, per sempre i sense tornada.\n\nEscriu {word} per confirmar:',
    en: 'This deletes your account and ALL your data, forever and with no going back.\n\nType {word} to confirm:',
  },
  'settings.deleteError': {
    es: 'No se ha podido borrar. Inténtalo de nuevo en un momento.',
    ca: 'No s’ha pogut esborrar. Torna-ho a provar d’aquí a un moment.',
    en: "Couldn't delete. Try again in a moment.",
  },
  'settings.home.title': {
    es: 'Tu casa',
    ca: 'La teva casa',
    'ca-valencia': 'La teua casa',
    en: 'Your home',
  },
  'settings.home.desc': {
    es: 'Para proponerte cosas que puedes hacer en casa durante un antojo.',
    ca: 'Per proposar-te coses que pots fer a casa durant unes ganes.',
    en: 'To suggest things you can do at home during a craving.',
  },
  'settings.pet.q': { es: '¿Tienes mascota?', ca: 'Tens mascota?', en: 'Do you have a pet?' },
  'settings.pet.none': { es: 'No', ca: 'No', en: 'No' },
  'settings.pet.dog': { es: 'Perro', ca: 'Gos', en: 'Dog' },
  'settings.pet.cat': { es: 'Gato', ca: 'Gat', en: 'Cat' },
  'settings.pet.other': { es: 'Otra', ca: 'Altra', en: 'Other' },
  'settings.pet.namePlaceholder': { es: '¿Cómo se llama? (opcional)', ca: 'Com es diu? (opcional)', en: "What's its name? (optional)" },
  'settings.pet.nameAria': {
    es: 'Nombre de tu mascota',
    ca: 'Nom de la teva mascota',
    'ca-valencia': 'Nom de la teua mascota',
    en: "Your pet's name",
  },
  'settings.plants': { es: 'Tengo plantas', ca: 'Tinc plantes', en: 'I have plants' },
  'settings.partner': {
    es: 'Vivo con mi pareja',
    ca: 'Visc amb la meva parella',
    'ca-valencia': 'Visc amb la meua parella',
    en: 'I live with my partner',
  },
  'settings.children': { es: 'Vivo con hijos', ca: 'Visc amb fills', en: 'I live with children' },
  'settings.data.title': {
    es: 'Tus datos',
    ca: 'Les teves dades',
    'ca-valencia': 'Les teues dades',
    en: 'Your data',
  },
  'settings.data.desc': {
    es: 'Todo lo tuyo es tuyo: descárgalo cuando quieras o vuelve a cargarlo.',
    ca: 'Tot el que és teu és teu: descarrega-ho quan vulguis o torna a carregar-ho.',
    en: 'Everything of yours is yours: download it whenever, or load it back.',
  },
  'settings.data.allJson': { es: 'Todo (JSON)', ca: 'Tot (JSON)', en: 'All (JSON)' },
  'settings.data.checkinsCsv': { es: 'Check-ins (CSV)', ca: 'Check-ins (CSV)', en: 'Check-ins (CSV)' },
  'settings.data.eventsCsv': { es: 'Cigarros (CSV)', ca: 'Cigarrets (CSV)', en: 'Cigarettes (CSV)' },
  'settings.data.cravingsCsv': { es: 'Antojos (CSV)', ca: 'Ganes (CSV)', en: 'Cravings (CSV)' },
  'settings.data.importJson': { es: 'Importar un JSON', ca: 'Importar un JSON', en: 'Import a JSON' },
  'settings.data.importing': { es: 'Importando…', ca: 'Important…', en: 'Importing…' },
  'settings.data.imported': {
    es: 'Importado: {events} cigarros, {checkins} check-ins, {journal} notas.',
    ca: 'Importat: {events} cigarrets, {checkins} check-ins, {journal} notes.',
    en: 'Imported: {events} cigarettes, {checkins} check-ins, {journal} notes.',
  },
  'settings.data.importError': {
    es: 'No he podido leer ese fichero. ¿Es un export de Respiria?',
    ca: 'No he pogut llegir aquest fitxer. És un export de Respiria?',
    en: "Couldn't read that file. Is it a Respiria export?",
  },

  // Health (Salud)
  'health.title': { es: 'Salud', ca: 'Salut', en: 'Health' },
  'health.emptyQuit': {
    es: 'Tu cuerpo empieza a recuperarse el {date}. Desde ese momento verás aquí cada avance, con la fecha exacta en que ocurre.',
    ca: 'El teu cos comença a recuperar-se el {date}. Des d’aquell moment veuràs aquí cada avanç, amb la data exacta en què passa.',
    en: 'Your body starts recovering on {date}. From then on you’ll see every step here, with the exact date it happens.',
  },
  'health.emptyNoQuit': {
    es: 'Cuando marques tu Día D verás aquí, uno a uno, los cambios que va haciendo tu cuerpo.',
    ca: 'Quan marquis el teu Dia D veuràs aquí, un a un, els canvis que va fent el teu cos.',
    en: 'Once you set your Quit Day you’ll see, one by one, the changes your body makes.',
  },
  'health.milestonesTitle': { es: 'Hitos conseguidos', ca: 'Fites aconseguides', en: 'Milestones reached' },
  'health.milestonesOf': { es: 'de {n}', ca: 'de {n}', en: 'of {n}' },
  'health.nextPre': { es: 'El siguiente, ', ca: 'El següent, ', en: 'Next up, ' },
  'health.nextSuffix': { es: ', {when}.', ca: ', {when}.', en: ', {when}.' },
  'health.allReached': { es: 'Los has alcanzado todos.', ca: 'Els has assolit tots.', en: 'You’ve reached them all.' },
  'health.footer': {
    es: 'Los plazos son los que describe la literatura médica general sobre dejar de fumar. Tu cuerpo lleva su propio ritmo.',
    ca: 'Els terminis són els que descriu la literatura mèdica general sobre deixar de fumar. El teu cos porta el seu propi ritme.',
    en: 'The timings are those described by general medical literature on quitting smoking. Your body goes at its own pace.',
  },

  // Check-in card
  'checkin.done': { es: 'Check-in hecho', ca: 'Check-in fet', en: 'Check-in done' },
  'checkin.today': { es: 'Check-in de hoy', ca: 'Check-in d’avui', en: "Today's check-in" },
  'checkin.invite': {
    es: 'Ánimo, energía, sueño… menos de un minuto.',
    ca: 'Ànim, energia, son… menys d’un minut.',
    en: 'Mood, energy, sleep… under a minute.',
  },
  'checkin.summaryMood': { es: 'ánimo {n}/5', ca: 'ànim {n}/5', en: 'mood {n}/5' },
  'checkin.summaryEnergy': { es: 'energía {n}/5', ca: 'energia {n}/5', en: 'energy {n}/5' },
  'checkin.summarySleep': { es: '{n} h de sueño', ca: '{n} h de son', en: '{n} h of sleep' },
  'checkin.summaryToday': { es: 'Hoy: {parts}', ca: 'Avui: {parts}', en: 'Today: {parts}' },
  'checkin.summaryEdit': { es: 'Toca para editarlo.', ca: 'Toca per editar-ho.', en: 'Tap to edit it.' },
  // Check-in sheet (form)
  'checkin.sheetTitle': { es: '¿Cómo ha ido el día?', ca: 'Com ha anat el dia?', en: 'How was your day?' },
  'checkin.water': { es: 'Agua', ca: 'Aigua', en: 'Water' },
  'checkin.note': { es: 'Una nota, si quieres', ca: 'Una nota, si vols', en: 'A note, if you like' },
  'checkin.saveBtn': { es: 'Guardar el check-in', ca: 'Desar el check-in', en: 'Save the check-in' },
  'checkin.updateBtn': { es: 'Actualizar', ca: 'Actualitzar', en: 'Update' },
  'checkin.allOptional': { es: 'Todo es opcional.', ca: 'Tot és opcional.', en: 'Everything is optional.' },
  'checkin.savedTitle': { es: 'Guardado', ca: 'Desat', en: 'Saved' },
  'checkin.savedBody': {
    es: 'Anotado. Con unos cuantos días verás en Progreso qué días te cuestan más y por qué.',
    ca: 'Anotat. Amb uns quants dies veuràs a Progrés quins dies et costen més i per què.',
    en: 'Noted. In a few days you’ll see in Progress which days are hardest and why.',
  },
  'checkin.continue': { es: 'Seguir', ca: 'Continuar', en: 'Continue' },

  // Money (Dinero)
  'money.saved': { es: 'Llevas ahorrado', ca: 'Portes estalviat', en: "You've saved" },
  'money.avoidedCount': {
    es: '{n} cigarros que no te has fumado',
    ca: '{n} cigarrets que no t’has fumat',
    en: "{n} cigarettes you haven't smoked",
  },
  'money.atThisRate': { es: 'A este ritmo', ca: 'A aquest ritme', en: 'At this rate' },
  'money.perDay': { es: 'Al día', ca: 'Al dia', en: 'Per day' },
  'money.perWeek': { es: 'A la semana', ca: 'A la setmana', en: 'Per week' },
  'money.perMonth': { es: 'Al mes', ca: 'Al mes', en: 'Per month' },
  'money.perYear': { es: 'Al año', ca: 'A l’any', en: 'Per year' },
  'money.projection': {
    es: 'Las cifras de semana, mes y año son una proyección de lo que dejas de gastar, no dinero que ya tengas.',
    ca: 'Les xifres de setmana, mes i any són una projecció del que deixes de gastar, no diners que ja tinguis.',
    en: 'The week, month and year figures are a projection of what you stop spending, not money you already have.',
  },
  'money.almostFor': { es: 'Ya casi te da para…', ca: 'Ja gairebé et dona per a…', en: 'Almost enough for…' },
  'money.more': { es: '{money} más', ca: '{money} més', en: '{money} more' },
  'money.progressTo': { es: 'Progreso hacia {label}', ca: 'Progrés cap a {label}', en: 'Progress toward {label}' },
  'money.alreadyPaid': {
    es: 'Lo que ya has pagado sin fumar',
    ca: 'El que ja has pagat sense fumar',
    en: "What you've already paid for by not smoking",
  },
  // Equivalencias de ahorro (EQUIVALENCES en shared/content). Es el único
  // catálogo de contenido que vive en código y no en BD, así que necesita sus
  // propias claves en vez de overrides del panel (issue #68).
  'equiv.meal-two': { es: 'un menú para dos', ca: 'un menú per a dos', en: 'a meal for two' },
  'equiv.indie-game': { es: 'un juego indie en Steam', ca: 'un joc indie a Steam', en: 'an indie game on Steam' },
  'equiv.good-dinner': { es: 'una cena buena', ca: 'un bon sopar', en: 'a good dinner' },
  'equiv.headphones': { es: 'unos auriculares decentes', ca: 'uns auriculars decents', en: 'decent headphones' },
  'equiv.keyboard': { es: 'un teclado mecánico', ca: 'un teclat mecànic', en: 'a mechanical keyboard' },
  'equiv.weekend-away': { es: 'un fin de semana fuera', ca: 'un cap de setmana fora', en: 'a weekend away' },
  'equiv.europe-flight': { es: 'un vuelo a Europa', ca: 'un vol a Europa', en: 'a flight in Europe' },
  'equiv.big-monitor': { es: 'un monitor grande', ca: 'un monitor gran', en: 'a big monitor' },
  'equiv.long-trip': { es: 'un viaje largo', ca: 'un viatge llarg', en: 'a long trip' },

  'money.goals': { es: 'Tus objetivos', ca: 'Els teus objectius', en: 'Your goals' },
  'money.goalsEmpty': {
    es: 'Ponle nombre a lo que quieres comprarte con lo que no te fumas. Verás cuánto te falta y para cuándo lo tendrás.',
    ca: 'Posa nom al que et vols comprar amb el que no et fumes. Veuràs quant et falta i per a quan el tindràs.',
    en: "Name what you want to buy with what you don't smoke. You'll see how much is left and when you'll have it.",
  },
  'money.achieved': { es: '¡Conseguido!', ca: 'Aconseguit!', en: 'Achieved!' },
  'money.goalProgress': { es: '{saved} de {target}', ca: '{saved} de {target}', en: '{saved} of {target}' },
  'money.eta': { es: 'A este ritmo, sobre el {date}', ca: 'A aquest ritme, cap al {date}', en: 'At this rate, around {date}' },
  'money.daysLeft': { es: '({n} días)', ca: '({n} dies)', en: '({n} days)' },
  'money.deleteGoalAria': { es: 'Borrar el objetivo {title}', ca: 'Esborrar l’objectiu {title}', en: 'Delete the goal {title}' },
  'money.newGoal': { es: 'Nuevo objetivo', ca: 'Nou objectiu', en: 'New goal' },
  'money.goalWhat': { es: '¿Qué quieres?', ca: 'Què vols?', en: 'What do you want?' },
  'money.goalWhatPlaceholder': { es: 'Una guitarra', ca: 'Una guitarra', en: 'A guitar' },
  'money.icon': { es: 'Icono', ca: 'Icona', en: 'Icon' },
  'money.iconAria': { es: 'Icono {icon}', ca: 'Icona {icon}', en: 'Icon {icon}' },
  'money.goalCost': { es: '¿Cuánto cuesta?', ca: 'Quant costa?', en: 'How much does it cost?' },
  'money.createGoal': { es: 'Crear objetivo', ca: 'Crear objectiu', en: 'Create goal' },

  // More (Más)
  // Suplantación de cuenta: solo la ve un admin, pero se traduce como todo lo demás.
  'more.impersonating.back': { es: 'Volver a mi cuenta', ca: 'Tornar al meu compte', en: 'Back to my account' },
  'more.impersonating.desc': {
    es: 'Estás viendo como un usuario de prueba',
    ca: 'Estàs veient com un usuari de prova',
    en: 'You are viewing as a test user',
  },
  'more.title': { es: 'Más', ca: 'Més', en: 'More' },
  'more.logCig': { es: 'Registrar un cigarro', ca: 'Registrar un cigarret', en: 'Log a cigarette' },
  'more.logCigDesc': {
    es: 'Sin juicios. Solo para tener el dato.',
    ca: 'Sense judicis. Només per tenir la dada.',
    en: 'No judgement. Just to have the data.',
  },
  'more.treatmentDay': { es: 'Día {day} de {total}', ca: 'Dia {day} de {total}', en: 'Day {day} of {total}' },
  'more.treatmentNoneDesc': {
    es: 'Cómo conseguirlas y empezar la pauta',
    ca: 'Com aconseguir-les i començar la pauta',
    en: 'How to get them and start the treatment',
  },
  'home.pills.title': {
    es: '¿Dejarlo con pastillas?',
    ca: 'Deixar-ho amb pastilles?',
    en: 'Quitting with pills?',
  },
  'home.pills.body': {
    es: 'Te explicamos cómo conseguirlas. Cuando las tengas, activa aquí la pauta.',
    ca: 'T’expliquem com aconseguir-les. Quan les tinguis, activa aquí la pauta.',
    en: 'We explain how to get them. Once you have them, start the treatment here.',
  },
  'more.money': { es: 'Dinero y objetivos', ca: 'Diners i objectius', en: 'Money and goals' },
  'more.moneyDesc': {
    es: 'Lo que llevas ahorrado y para qué te da',
    ca: 'El que portes estalviat i per a què et dona',
    en: "What you've saved and what it's enough for",
  },
  'more.emergency': { es: 'Modo emergencia', ca: 'Mode emergència', en: 'Emergency mode' },
  'more.emergencyDesc': {
    es: 'Cuando el antojo aprieta de verdad',
    ca: 'Quan les ganes pitgen de veritat',
    en: 'When the craving really hits',
  },
  'more.journalDesc': {
    es: 'Escribe y busca en lo que has escrito',
    ca: 'Escriu i cerca en el que has escrit',
    en: "Write and search what you've written",
  },
  'more.notifDesc': { es: 'Tomas, check-in e hitos', ca: 'Preses, check-in i fites', en: 'Doses, check-in and milestones' },
  'more.settingsDesc': {
    es: 'Tu Día D, tu tabaco, apariencia',
    ca: 'El teu Dia D, el teu tabac, aparença',
    en: 'Your Quit Day, tobacco, appearance',
  },
  'more.panel': { es: 'Panel', ca: 'Panell', en: 'Panel' },
  'more.panelDesc': { es: 'Usuarios de prueba, gestión', ca: 'Usuaris de prova, gestió', en: 'Test users, management' },
  'more.logout': { es: 'Cerrar sesión', ca: 'Tancar sessió', en: 'Log out' },

  // Journal (Diario)
  'journal.title': { es: 'Diario', ca: 'Diari', en: 'Journal' },
  'journal.write': { es: 'Escribir', ca: 'Escriure', en: 'Write' },
  'journal.searchPlaceholder': {
    es: 'Buscar en lo que has escrito',
    ca: 'Cerca en el que has escrit',
    en: "Search what you've written",
  },
  'journal.searchAria': { es: 'Buscar en el diario', ca: 'Cercar al diari', en: 'Search the journal' },
  'journal.clearSearch': { es: 'Limpiar la búsqueda', ca: 'Netejar la cerca', en: 'Clear search' },
  'journal.filterMoodAria': {
    es: 'Filtrar por ánimo {n} de 5',
    ca: 'Filtrar per ànim {n} de 5',
    en: 'Filter by mood {n} of 5',
  },
  'journal.emptyFilter': {
    es: 'No hay nada que coincida con eso.',
    ca: 'No hi ha res que coincideixi amb això.',
    en: 'Nothing matches that.',
  },
  'journal.emptyIntro': {
    es: 'Escribir lo que pasa por la cabeza ayuda más de lo que parece, sobre todo los días malos. Para empezar:',
    ca: 'Escriure el que et passa pel cap ajuda més del que sembla, sobretot els dies dolents. Per començar:',
    en: 'Writing down what’s on your mind helps more than it seems, especially on bad days. To start:',
  },
  'journal.prompt1': {
    es: '¿Qué ha sido lo más difícil de hoy?',
    ca: 'Què ha sigut el més difícil d’avui?',
    en: 'What was the hardest thing about today?',
  },
  'journal.prompt2': {
    es: '¿En qué momento has estado a punto y no has caído?',
    ca: 'En quin moment has estat a punt i no has caigut?',
    en: 'When were you close and didn’t give in?',
  },
  'journal.prompt3': {
    es: '¿Qué te ha ayudado hoy que quieras recordar?',
    ca: 'Què t’ha ajudat avui que vulguis recordar?',
    en: 'What helped you today that you want to remember?',
  },
  'journal.prompt4': {
    es: '¿Qué le dirías a quien empieza mañana?',
    ca: 'Què li diries a qui comença demà?',
    en: 'What would you tell someone starting tomorrow?',
  },
  'journal.prompt5': {
    es: '¿Cómo te has notado el cuerpo hoy?',
    ca: 'Com t’has notat el cos avui?',
    en: 'How did your body feel today?',
  },
  'journal.entryMoodAria': { es: 'Ánimo {n} de 5', ca: 'Ànim {n} de 5', en: 'Mood {n} of 5' },
  'journal.deleteEntry': { es: 'Borrar esta entrada', ca: 'Esborrar aquesta entrada', en: 'Delete this entry' },
  'journal.entryAria': { es: 'Tu entrada del diario', ca: 'La teva entrada del diari', en: 'Your journal entry' },
  'journal.howAreYou': { es: '¿Cómo estás?', ca: 'Com estàs?', en: 'How are you?' },

  // Progress (Progreso) — shell + achievements
  'progress.title': { es: 'Progreso', ca: 'Progrés', en: 'Progress' },
  'progress.tabStats': { es: 'Estadísticas', ca: 'Estadístiques', en: 'Statistics' },
  'progress.tabCalendar': { es: 'Calendario', ca: 'Calendari', en: 'Calendar' },
  'progress.tabAchievements': { es: 'Logros', ca: 'Assoliments', en: 'Achievements' },
  'progress.cat.all': { es: 'Todos', ca: 'Tots', en: 'All' },
  'progress.cat.time': { es: 'Tiempo', ca: 'Temps', en: 'Time' },
  'progress.cat.money': { es: 'Dinero', ca: 'Diners', en: 'Money' },
  'progress.cat.avoided': { es: 'Evitados', ca: 'Evitats', en: 'Avoided' },
  'progress.cat.craving': { es: 'Antojos', ca: 'Ganes', en: 'Cravings' },
  'progress.cat.treatment': { es: 'Tratamiento', ca: 'Tractament', en: 'Treatment' },
  'progress.cat.habit': { es: 'Hábito', ca: 'Hàbit', en: 'Habit' },
  'progress.cat.home': { es: 'Casa', ca: 'Casa', en: 'Home' },
  'progress.cat.maintenance': { es: 'Mantenimiento', ca: 'Manteniment', en: 'Maintenance' },
  'progress.cat.situations': { es: 'Situaciones', ca: 'Situacions', en: 'Situations' },
  'progress.levelAria': { es: 'Progreso de nivel', ca: 'Progrés de nivell', en: 'Level progress' },
  'progress.xOfY': { es: '{a} de {b}', ca: '{a} de {b}', en: '{a} of {b}' },
  'progress.unlockedAria': { es: 'Logros desbloqueados', ca: 'Assoliments desbloquejats', en: 'Achievements unlocked' },
  'progress.reached': { es: 'Conseguido', ca: 'Aconseguit', en: 'Reached' },
  'progress.progressOfAria': { es: 'Progreso de {title}', ca: 'Progrés de {title}', en: 'Progress of {title}' },

  // Stats (Estadísticas)
  'stats.7d': { es: '7 días', ca: '7 dies', en: '7 days' },
  'stats.30d': { es: '30 días', ca: '30 dies', en: '30 days' },
  'stats.90d': { es: '90 días', ca: '90 dies', en: '90 days' },
  'stats.all': { es: 'Todo', ca: 'Tot', en: 'All' },
  'stats.wd.mon': { es: 'Lun', ca: 'Dl', en: 'Mon' },
  'stats.wd.tue': { es: 'Mar', ca: 'Dt', en: 'Tue' },
  'stats.wd.wed': { es: 'Mié', ca: 'Dc', en: 'Wed' },
  'stats.wd.thu': { es: 'Jue', ca: 'Dj', en: 'Thu' },
  'stats.wd.fri': { es: 'Vie', ca: 'Dv', en: 'Fri' },
  'stats.wd.sat': { es: 'Sáb', ca: 'Ds', en: 'Sat' },
  'stats.wd.sun': { es: 'Dom', ca: 'Dg', en: 'Sun' },
  'stats.notEnough': {
    es: 'Todavía no hay suficiente para sacar conclusiones. En cuanto lleves unos días registrando antojos aparecerán aquí tus horas difíciles, tus disparadores y qué misión te funciona mejor.',
    ca: 'Encara no n’hi ha prou per treure conclusions. Tan bon punt portis uns dies registrant les ganes, apareixeran aquí les teves hores difícils, els teus disparadors i quina missió et funciona millor.',
    en: 'There isn’t enough yet to draw conclusions. Once you’ve logged cravings for a few days, your hard hours, your triggers and which mission works best will show up here.',
  },
  'stats.cravingsLogged': { es: 'Antojos registrados', ca: 'Ganes registrades', en: 'Cravings logged' },
  'stats.held': { es: 'Aguantados', ca: 'Aguantats', en: 'Resisted' },
  'stats.avgIntensity': { es: 'Intensidad media', ca: 'Intensitat mitjana', en: 'Avg. intensity' },
  'stats.avgDuration': { es: 'Duración media', ca: 'Durada mitjana', en: 'Avg. duration' },
  'stats.hardHours': { es: 'Tus horas difíciles', ca: 'Les teves hores difícils', en: 'Your hard hours' },
  'stats.hardHoursAria': {
    es: 'Momentos difíciles por hora del día. El pico está a las {hour}:00.',
    ca: 'Moments difícils per hora del dia. El pic és a les {hour}:00.',
    en: 'Hard moments by hour of day. The peak is at {hour}:00.',
  },
  'stats.hardHoursAriaNoPeak': {
    es: 'Momentos difíciles por hora del día.',
    ca: 'Moments difícils per hora del dia.',
    en: 'Hard moments by hour of day.',
  },
  'stats.byWeekday': { es: 'Por día de la semana', ca: 'Per dia de la setmana', en: 'By day of week' },
  'stats.byWeekdayAria': {
    es: 'Momentos difíciles por día de la semana.',
    ca: 'Moments difícils per dia de la setmana.',
    en: 'Hard moments by day of week.',
  },
  'stats.weekMap': { es: 'Mapa de la semana', ca: 'Mapa de la setmana', en: 'Week map' },
  'stats.weekMapDesc': {
    es: 'Cuanto más intenso el color, más te ha costado esa hora de ese día.',
    ca: 'Com més intens el color, més t’ha costat aquella hora d’aquell dia.',
    en: 'The deeper the colour, the harder that hour of that day was.',
  },
  'stats.weekMapAria': {
    es: 'Mapa de calor de hora por día de la semana.',
    ca: 'Mapa de calor d’hora per dia de la setmana.',
    en: 'Heatmap of hour by day of week.',
  },
  'stats.cravingsPerDay': { es: 'Antojos por día', ca: 'Ganes per dia', en: 'Cravings per day' },
  'stats.cravingsPerDayAria': {
    es: 'Antojos registrados cada día del periodo.',
    ca: 'Ganes registrades cada dia del període.',
    en: 'Cravings logged each day of the period.',
  },
  'stats.triggers': { es: 'Qué te lo dispara', ca: 'Què t’ho dispara', en: 'What sets it off' },
  'stats.bestMission': { es: 'Qué misión te funciona', ca: 'Quina missió et funciona', en: 'Which mission works for you' },
  'stats.missionSuccess': {
    es: '{pct}% de acierto en {n} intentos',
    ca: '{pct}% d’encert en {n} intents',
    en: '{pct}% success over {n} tries',
  },
  'stats.worstMission': {
    es: 'La que menos te sirve es «{label}», con un {pct}%. Puedes cambiarla cuando te salga.',
    ca: 'La que menys t’ajuda és «{label}», amb un {pct}%. La pots canviar quan et surti.',
    en: 'The one that helps least is “{label}”, at {pct}%. You can swap it when it comes up.',
  },
  'stats.feelings': { es: 'Cómo te has sentido', ca: 'Com t’has sentit', en: 'How you’ve felt' },
  'stats.mood': { es: 'Ánimo', ca: 'Ànim', en: 'Mood' },
  'stats.energy': { es: 'Energía', ca: 'Energia', en: 'Energy' },
  'stats.stress': { es: 'Estrés', ca: 'Estrès', en: 'Stress' },
  'stats.sleep': { es: 'Sueño (h)', ca: 'Son (h)', en: 'Sleep (h)' },
  'stats.feelingsNote': {
    es: 'Estas cuatro salen de tus check-ins. Los días que no lo hiciste quedan en blanco.',
    ca: 'Aquestes quatre surten dels teus check-ins. Els dies que no el vas fer queden en blanc.',
    en: 'These four come from your check-ins. Days you skipped it are left blank.',
  },
  'stats.metricAria': { es: 'Evolución de {label}', ca: 'Evolució de {label}', en: 'Trend of {label}' },

  // Calendar
  'cal.wd0': { es: 'L', ca: 'Dl', en: 'M' },
  'cal.wd1': { es: 'M', ca: 'Dt', en: 'T' },
  'cal.wd2': { es: 'X', ca: 'Dc', en: 'W' },
  'cal.wd3': { es: 'J', ca: 'Dj', en: 'T' },
  'cal.wd4': { es: 'V', ca: 'Dv', en: 'F' },
  'cal.wd5': { es: 'S', ca: 'Ds', en: 'S' },
  'cal.wd6': { es: 'D', ca: 'Dg', en: 'S' },
  'cal.prevMonth': { es: 'Mes anterior', ca: 'Mes anterior', en: 'Previous month' },
  'cal.nextMonth': { es: 'Mes siguiente', ca: 'Mes següent', en: 'Next month' },
  'cal.status.clean': { es: 'sin fumar', ca: 'sense fumar', en: 'smoke-free' },
  'cal.status.slip': { es: 'con un desliz', ca: 'amb una relliscada', en: 'with a slip' },
  'cal.status.relapse': { es: 'con una recaída', ca: 'amb una recaiguda', en: 'with a relapse' },
  'cal.status.preQuit': { es: 'antes del Día D', ca: 'abans del Dia D', en: 'before the Quit Day' },
  'cal.status.future': { es: 'todavía por llegar', ca: 'encara per arribar', en: 'still to come' },
  'cal.legend.clean': { es: 'Sin fumar', ca: 'Sense fumar', en: 'Smoke-free' },
  'cal.legend.slip': { es: 'Un desliz', ca: 'Una relliscada', en: 'A slip' },
  'cal.legend.relapse': { es: 'Recaída', ca: 'Recaiguda', en: 'Relapse' },
  'cal.legend.preQuit': { es: 'Antes del Día D', ca: 'Abans del Dia D', en: 'Before the Quit Day' },
  'cal.cleanDays': { es: 'días sin fumar', ca: 'dies sense fumar', en: 'smoke-free days' },
  'cal.slipDays': { es: 'con desliz', ca: 'amb relliscada', en: 'with a slip' },
  'cal.cravings': { es: 'antojos', ca: 'ganes', en: 'cravings' },
  'cal.checkins': { es: 'check-ins', ca: 'check-ins', en: 'check-ins' },
  'cal.howToRead': { es: 'Cómo se lee', ca: 'Com es llegeix', en: 'How to read it' },
  'cal.checkinDot': {
    es: 'El punto de debajo indica que ese día hiciste el check-in.',
    ca: 'El punt de sota indica que aquell dia vas fer el check-in.',
    en: 'The dot underneath means you did the check-in that day.',
  },
  'cal.dayAria': { es: 'Día {n}, {label}', ca: 'Dia {n}, {label}', en: 'Day {n}, {label}' },

  // Day detail
  'day.empty': {
    es: 'Ese día no quedó registrado nada. Tampoco pasa nada.',
    ca: 'Aquell dia no va quedar registrat res. Tampoc passa res.',
    en: 'Nothing was logged that day. That’s fine too.',
  },
  'day.howYouWere': { es: 'Cómo estabas', ca: 'Com estaves', en: 'How you were' },
  'day.mood': { es: 'Ánimo', ca: 'Ànim', en: 'Mood' },
  'day.energy': { es: 'Energía', ca: 'Energia', en: 'Energy' },
  'day.stress': { es: 'Estrés', ca: 'Estrès', en: 'Stress' },
  'day.sleep': { es: 'Sueño', ca: 'Son', en: 'Sleep' },
  'day.exercise': { es: 'Ejercicio', ca: 'Exercici', en: 'Exercise' },
  'day.dosesTitle': { es: 'Tomas · {taken} de {total}', ca: 'Preses · {taken} de {total}', en: 'Doses · {taken} of {total}' },
  'day.cravingsTitle': { es: 'Antojos · {n}', ca: 'Ganes · {n}', en: 'Cravings · {n}' },
  'day.cigarettes': { es: 'Cigarros', ca: 'Cigarrets', en: 'Cigarettes' },
  'day.achievements': { es: 'Logros de ese día', ca: 'Assoliments d’aquell dia', en: 'That day’s achievements' },
  'day.outcome.resisted': { es: 'aguantado', ca: 'aguantat', en: 'resisted' },
  'day.outcome.partial': { es: 'aguantado a medias', ca: 'aguantat a mitges', en: 'partly resisted' },
  'day.outcome.smoked': { es: 'acabó en cigarro', ca: 'va acabar en cigarret', en: 'ended in a cigarette' },
  'day.outcome.abandoned': { es: 'sin cerrar', ca: 'sense tancar', en: 'not closed' },
  'day.notClosed': { es: 'sin cerrar', ca: 'sense tancar', en: 'not closed' },
  'day.intensitySuffix': { es: ' · intensidad {n}/5', ca: ' · intensitat {n}/5', en: ' · intensity {n}/5' },
  'day.cigsN': { es: '{n} cigarros', ca: '{n} cigarrets', en: '{n} cigarettes' },
  'day.cig1': { es: '1 cigarro', ca: '1 cigarret', en: '1 cigarette' },

  // Emergency mode
  'emergency.exitAria': { es: 'Salir del modo emergencia', ca: 'Sortir del mode emergència', en: 'Exit emergency mode' },
  'emergency.breatheTitle': { es: 'Respira conmigo', ca: 'Respira amb mi', en: 'Breathe with me' },
  'emergency.breatheSub': {
    es: 'Un minuto. No tienes que hacer nada más.',
    ca: 'Un minut. No has de fer res més.',
    en: 'One minute. You don’t have to do anything else.',
  },
  'emergency.skip': { es: 'Saltar', ca: 'Saltar', en: 'Skip' },
  'emergency.holdEnd': { es: 'Aguanta hasta el final.', ca: 'Aguanta fins al final.', en: 'Hold on till the end.' },
  'emergency.toMission': { es: 'Pasar a la misión', ca: 'Passar a la missió', en: 'Go to the mission' },
  'emergency.moveBody': {
    es: 'Mueve el cuerpo. Es lo que corta el antojo de raíz, mejor que pensar en no fumar.',
    ca: 'Mou el cos. És el que talla les ganes d’arrel, millor que pensar a no fumar.',
    en: 'Move your body. That’s what cuts the craving at the root, better than thinking about not smoking.',
  },
  'emergency.imReady': { es: 'Ya estoy', ca: 'Ja estic', en: "I'm ready" },
  'emergency.passed': { es: '¿Ha pasado?', ca: 'Ha passat?', en: 'Has it passed?' },
  'emergency.yesPassed': { es: 'Sí, ha pasado', ca: 'Sí, ha passat', en: 'Yes, it passed' },
  'emergency.anotherRound': { es: 'Aún no, otra vuelta', ca: 'Encara no, una altra volta', en: 'Not yet, another round' },
  'emergency.smoked': { es: 'He fumado', ca: 'He fumat', en: 'I smoked' },
  'emergency.exitConfirm': { es: '¿Salir de emergencia?', ca: 'Sortir d’emergència?', en: 'Exit emergency?' },
  'emergency.exitConfirmSub': {
    es: 'Puedes quedarte todo el tiempo que necesites. No hay prisa.',
    ca: 'Pots quedar-t’hi tot el temps que necessitis. No hi ha pressa.',
    en: 'You can stay as long as you need. There’s no rush.',
  },
  'emergency.stay': { es: 'Me quedo', ca: 'Em quedo', en: "I'll stay" },
  'emergency.exit': { es: 'Salir', ca: 'Sortir', en: 'Exit' },
  'emergency.ctxDefault': {
    es: 'Cada segundo que pasa, el antojo pierde fuerza.',
    ca: 'Cada segon que passa, les ganes perden força.',
    en: 'Every second that passes, the craving loses strength.',
  },
  'emergency.ctxCountdown': {
    es: 'Te quedan {days} {unit} para tu Día D. Ya has empezado.',
    ca: 'Et queden {days} {unit} per al teu Dia D. Ja has començat.',
    en: '{days} {unit} left until your Quit Day. You’ve already started.',
  },
  'emergency.ctxStreak': {
    es: 'Llevas {duration} sin fumar.',
    ca: 'Portes {duration} sense fumar.',
    en: 'You’ve gone {duration} without smoking.',
  },
  'emergency.ctxSaved': { es: 'Has ahorrado {money}.', ca: 'Has estalviat {money}.', en: 'You’ve saved {money}.' },
  'emergency.ctxAvoided': {
    es: 'Son {n} cigarros que no te has fumado.',
    ca: 'Són {n} cigarrets que no t’has fumat.',
    en: 'That’s {n} cigarettes you haven’t smoked.',
  },
  'emergency.ctxWon': {
    es: 'Has ganado esta batalla {n} {unit}. Sabes cómo se hace.',
    ca: 'Has guanyat aquesta batalla {n} {unit}. Saps com es fa.',
    en: 'You’ve won this battle {n} {unit}. You know how it’s done.',
  },
  'emergency.time': { es: 'vez', ca: 'vegada', en: 'time' },
  'emergency.times': { es: 'veces', ca: 'vegades', en: 'times' },
  'emergency.ctxAlways': { es: 'Esto se pasa. Siempre se pasa.', ca: 'Això passa. Sempre passa.', en: 'This passes. It always passes.' },
  'emergency.mission1.label': { es: '20 sentadillas', ca: '20 esquats', en: '20 squats' },
  'emergency.mission1.desc': { es: 'Sin contar rápido. Nota las piernas.', ca: 'Sense comptar ràpid. Nota les cames.', en: 'Don’t rush the count. Feel your legs.' },
  'emergency.mission2.label': { es: 'Sal a la calle', ca: 'Surt al carrer', en: 'Go outside' },
  'emergency.mission2.desc': { es: 'Da la vuelta a la manzana, aunque sea despacio.', ca: 'Fes la volta a l’illa, encara que sigui a poc a poc.', en: 'Walk around the block, even slowly.' },
  'emergency.mission3.label': { es: 'Lávate la cara con agua fría', ca: 'Renta’t la cara amb aigua freda', en: 'Splash cold water on your face' },
  'emergency.mission3.desc': { es: 'Corta el impulso en seco.', ca: 'Talla l’impuls en sec.', en: 'Cuts the urge dead.' },
  'emergency.mission4.label': { es: 'Sube y baja las escaleras', ca: 'Puja i baixa les escales', en: 'Go up and down the stairs' },
  'emergency.mission4.desc': { es: 'Dos pisos, sin parar.', ca: 'Dos pisos, sense parar.', en: 'Two floors, no stopping.' },
  'emergency.mission5.label': { es: '15 flexiones', ca: '15 flexions', en: '15 push-ups' },
  'emergency.mission5.desc': { es: 'De rodillas también valen.', ca: 'De genolls també valen.', en: 'On your knees counts too.' },

  // Login
  // ── Landing pública (#78) ───────────────────────────────────────────────
  // Tres idiomas por decisión del usuario: castellano, valenciano e inglés. El valenciano
  // hereda del catalán y solo lleva entrada propia donde la forma difiere.
  'landing.signIn': { es: 'Entrar', ca: 'Entrar', en: 'Sign in' },
  'landing.heroTitle': {
    es: 'Dejar de fumar, sin que nadie te riña',
    ca: 'Deixar de fumar, sense que ningú et renyi',
    en: 'Quit smoking without being told off',
  },
  'landing.heroSubtitle': {
    es: 'Respiria acompaña tu tratamiento: te avisa de cada toma, te enseña lo que tu cuerpo va recuperando y lo que dejas de gastar. Un día a la vez.',
    ca: 'Respiria acompanya el teu tractament: t’avisa de cada presa, et mostra el que el teu cos va recuperant i el que deixes de gastar. Un dia a la vegada.',
    'ca-valencia':
      'Respiria acompanya el teu tractament: t’avisa de cada presa, et mostra el que el teu cos va recuperant i el que deixes de gastar. Un dia a la vegada.',
    en: 'Respiria walks your treatment with you: it reminds you of every pill, shows what your body is getting back and what you stop spending. One day at a time.',
  },
  'landing.ctaStart': { es: 'Empezar gratis', ca: 'Començar gratis', en: 'Start free' },
  'landing.ctaDemo': { es: 'Ver la app por dentro', ca: 'Veure l’app per dins', en: 'See inside the app' },
  'landing.ctaNote': {
    es: 'Sin instalar nada y sin tarjeta. La demo entra en una cuenta de ejemplo.',
    ca: 'Sense instal·lar res i sense targeta. La demo entra en un compte d’exemple.',
    en: 'Nothing to install, no card. The demo opens a sample account.',
  },
  'landing.honestAngle': {
    es: 'Aunque no lo dejes a la primera, en dos semanas vas a saber cuánto fumas, a qué horas y en qué situaciones. Eso solo ya cambia las cosas.',
    ca: 'Encara que no ho deixes a la primera, en dues setmanes sabràs quant fumes, a quines hores i en quines situacions. Això tot sol ja canvia les coses.',
    en: 'Even if you don’t quit on the first try, within two weeks you’ll know how much you smoke, when and in which situations. That alone changes things.',
  },

  'landing.f1Title': {
    es: 'Tu caja de pastillas, sin cuentas mentales',
    ca: 'La teua caixa de pastilles, sense comptes mentals',
    en: 'Your pill box, without the mental arithmetic',
  },
  'landing.f1Body': {
    es: 'Las 100 tomas de la citisina tienen horario, y fallar una es fácil. Respiria te dice cuál toca y cuándo, lleva la cuenta de las que quedan y te enseña la racha sin dramatizar el día que se tuerce.',
    ca: 'Les 100 preses de la citisina tenen horari, i fallar-ne una és fàcil. Respiria et diu quina toca i quan, porta el compte de les que queden i et mostra la ratxa sense dramatitzar el dia que es torça.',
    en: 'Cytisine’s 100 doses run on a schedule, and missing one is easy. Respiria tells you which is next and when, counts what’s left, and shows your streak without making a drama of a bad day.',
  },
  'landing.f1Alt': {
    es: 'Pantalla de inicio: racha sin fumar, dinero ahorrado y la toma siguiente del tratamiento',
    ca: 'Pantalla d’inici: ratxa sense fumar, diners estalviats i la presa següent del tractament',
    en: 'Home screen: smoke-free streak, money saved and the next treatment dose',
  },
  'landing.f2Title': {
    es: 'Lo que tu cuerpo recupera, con fecha',
    ca: 'El que el teu cos recupera, amb data',
    en: 'What your body gets back, with dates',
  },
  'landing.f2Body': {
    es: 'El pulso, el monóxido de carbono, el sabor, la respiración. Cada hito con el día exacto en que te toca, para que el progreso deje de ser una idea abstracta.',
    ca: 'El pols, el monòxid de carboni, el sabor, la respiració. Cada fita amb el dia exacte que et toca, perquè el progrés deixe de ser una idea abstracta.',
    en: 'Your pulse, carbon monoxide, taste, breathing. Every milestone with the exact day it lands, so progress stops being an abstract idea.',
  },
  'landing.f2Alt': {
    es: 'Pantalla de salud: hitos conseguidos y la línea de recuperación con fechas',
    ca: 'Pantalla de salut: fites aconseguides i la línia de recuperació amb dates',
    en: 'Health screen: milestones reached and the recovery timeline with dates',
  },
  'landing.f3Title': {
    es: 'El dinero que vuelve a ser tuyo',
    ca: 'Els diners que tornen a ser teus',
    en: 'The money that’s yours again',
  },
  'landing.f3Body': {
    es: 'Lo que llevas ahorrado, al día, al mes y al año, y en qué te daría para gastarlo. No es una proyección optimista: sale de lo que tú fumabas y de lo que cuesta tu tabaco.',
    ca: 'El que portes estalviat, al dia, al mes i a l’any, i per a què et donaria. No és una projecció optimista: ix del que tu fumaves i del que costa el teu tabac.',
    en: 'What you’ve saved so far — daily, monthly and yearly — and what it would buy. Not an optimistic projection: it comes from what you smoked and what your tobacco costs.',
  },
  'landing.f3Alt': {
    es: 'Pantalla de dinero: total ahorrado y a qué ritmo',
    ca: 'Pantalla de diners: total estalviat i a quin ritme',
    en: 'Money screen: total saved and at what rate',
  },

  'landing.promiseTitle': {
    es: 'Lo que esta app no va a hacer',
    ca: 'El que esta app no farà',
    en: 'What this app will not do',
  },
  'landing.promise1': {
    es: 'No te va a llamar fracaso. Un cigarro se registra, se acompaña y se sigue.',
    ca: 'No et dirà fracàs. Un cigarret es registra, s’acompanya i es continua.',
    en: 'It will not call you a failure. A cigarette gets logged, supported, and you carry on.',
  },
  'landing.promise2': {
    es: 'No te va a borrar el progreso. Una recaída rompe la racha actual, nunca el histórico ni lo que has ahorrado.',
    ca: 'No t’esborrarà el progrés. Una recaiguda trenca la ratxa actual, mai l’històric ni el que has estalviat.',
    en: 'It will not wipe your progress. A slip breaks the current streak, never your history or your savings.',
  },
  'landing.promise3': {
    es: 'No sustituye a tu médico. Es el acompañante del tratamiento que te han recetado, no un consejo médico.',
    ca: 'No substitueix el teu metge. És l’acompanyant del tractament que t’han receptat, no un consell mèdic.',
    en: 'It does not replace your doctor. It accompanies the treatment you were prescribed; it is not medical advice.',
  },

  'landing.closingTitle': {
    es: 'No te fíes: míralo por dentro',
    ca: 'No te’n fies: mira-ho per dins',
    en: 'Don’t take our word for it',
  },
  'landing.closingBody': {
    es: 'Entra en una cuenta de ejemplo con datos reales de uso y mira las pantallas de verdad antes de registrarte.',
    ca: 'Entra en un compte d’exemple amb dades reals d’ús i mira les pantalles de veritat abans de registrar-te.',
    en: 'Open a sample account with real usage data and see the actual screens before signing up.',
  },
  'landing.medicalNote': {
    es: 'Respiria no da consejo médico ni sustituye a un profesional sanitario. Si estás en tratamiento, sigue siempre las indicaciones de quien te lo ha recetado.',
    ca: 'Respiria no dóna consell mèdic ni substitueix un professional sanitari. Si estàs en tractament, segueix sempre les indicacions de qui te l’ha receptat.',
    en: 'Respiria does not give medical advice and does not replace a healthcare professional. If you are on treatment, always follow the instructions of whoever prescribed it.',
  },
  'landing.legal': { es: 'Aviso legal y privacidad', ca: 'Avís legal i privacitat', en: 'Legal notice and privacy' },

  'login.tagline': {
    es: 'Tu compañero para dejar de fumar. Un día, un antojo y una respiración cada vez.',
    ca: 'El teu company per deixar de fumar. Un dia, unes ganes i una respiració cada vegada.',
    en: 'Your companion for quitting smoking. One day, one craving and one breath at a time.',
  },
  'login.google': { es: 'Entrar con Google', ca: 'Entrar amb Google', en: 'Sign in with Google' },
  'login.demoAs': { es: 'Entrar como {name} (demo)', ca: 'Entrar com a {name} (demo)', en: 'Enter as {name} (demo)' },
  'login.demoRegister': { es: 'Probar el registro (demo)', ca: 'Provar el registre (demo)', en: 'Try the sign-up (demo)' },
  'waiting.title': { es: 'Estás en la lista', ca: 'Estàs a la llista', en: 'You’re on the list' },
  'waiting.body': {
    es: 'Respiria está en beta privada. Tu acceso está pendiente de aprobación; te avisaremos en cuanto puedas entrar.',
    ca: 'Respiria està en beta privada. El teu accés està pendent d’aprovació; t’avisarem quan puguis entrar.',
    en: 'Respiria is in private beta. Your access is pending approval; we’ll let you know as soon as you can get in.',
  },
  'waiting.blocked': {
    es: 'Tu acceso no está disponible ahora mismo.',
    ca: 'El teu accés no està disponible ara mateix.',
    en: 'Your access isn’t available right now.',
  },
  'waiting.logout': { es: 'Cerrar sesión', ca: 'Tancar sessió', en: 'Sign out' },
  'login.dataNote': {
    es: 'Guardamos tus datos para mostrarte tu progreso. Puedes exportarlos o borrar tu cuenta cuando quieras.',
    ca: 'Guardem les teves dades per mostrar-te el teu progrés. Les pots exportar o esborrar el teu compte quan vulguis.',
    en: 'We store your data to show you your progress. You can export it or delete your account whenever you want.',
  },

  // Share progress
  'share.button': { es: 'Compartir mi progreso', ca: 'Compartir el meu progrés', en: 'Share my progress' },
  'share.imageButton': { es: 'Imagen para stories', ca: 'Imatge per a stories', en: 'Image for stories' },
  'share.card.tagline': {
    es: 'Mi camino para dejar de fumar',
    ca: 'El meu camí per deixar de fumar',
    en: 'My journey to quit smoking',
  },
  'share.card.days.one': { es: 'día sin fumar', ca: 'dia sense fumar', en: 'day smoke-free' },
  'share.card.days.other': { es: 'días sin fumar', ca: 'dies sense fumar', en: 'days smoke-free' },
  'share.card.saved': { es: 'ahorrados', ca: 'estalviats', en: 'saved' },
  'share.card.avoided': { es: 'cigarros evitados', ca: 'cigarrets evitats', en: 'cigarettes avoided' },
  'share.card.cravings': { es: 'antojos superados', ca: 'ganes superades', en: 'cravings beaten' },
  'share.text': {
    es: '🚭 Llevo {days} días sin fumar con Respiria.\n💶 {money} ahorrados · 🚬 {cigs} cigarros que no me he fumado · 💪 {cravings} antojos superados.\nPaso a paso. 💪\nrespiria.isaies.com',
    ca: '🚭 Porto {days} dies sense fumar amb Respiria.\n💶 {money} estalviats · 🚬 {cigs} cigarrets que no m’he fumat · 💪 {cravings} ganes superades.\nPas a pas. 💪\nrespiria.isaies.com',
    en: '🚭 {days} days smoke-free with Respiria.\n💶 {money} saved · 🚬 {cigs} cigarettes not smoked · 💪 {cravings} cravings beaten.\nStep by step. 💪\nrespiria.isaies.com',
  },

  // Onboarding (registro)
  'onboarding.back': { es: 'Atrás', ca: 'Enrere', en: 'Back' },
  'onboarding.continue': { es: 'Continuar', ca: 'Continuar', en: 'Continue' },
  'onboarding.start': { es: 'Empezar', ca: 'Començar', en: 'Start' },
  'onboarding.saveError': {
    es: 'No se ha podido guardar. Revisa la conexión y vuelve a intentarlo.',
    ca: 'No s’ha pogut desar. Revisa la connexió i torna-ho a provar.',
    en: 'Could not save. Check your connection and try again.',
  },
  'onboarding.consentPre': { es: 'He leído y acepto la', ca: 'He llegit i accepto la', en: 'I have read and accept the' },
  'onboarding.consentLink': {
    es: 'política de privacidad y los términos',
    ca: 'política de privacitat i els termes',
    en: 'privacy policy and terms',
  },
  'onboarding.consentPost': {
    es: ', y doy mi consentimiento para tratar mis datos de salud dentro de la app.',
    ca: ', i dono el meu consentiment per tractar les meves dades de salut dins l’app.',
    en: ', and I consent to processing my health data within the app.',
  },

  'onboarding.welcomeTitle': { es: 'Bienvenido a Respiria', ca: 'Benvingut a Respiria', en: 'Welcome to Respiria' },
  'onboarding.welcomeSub': {
    es: 'Vamos a configurar la app en menos de un minuto. Nada de esto es definitivo: todo se puede cambiar después en Ajustes.',
    ca: 'Configurarem l’app en menys d’un minut. Res d’això és definitiu: tot es pot canviar després a Ajustos.',
    en: 'Let’s set up the app in under a minute. None of this is final: you can change it all later in Settings.',
  },

  'onboarding.langNote': {
    es: 'Elige tu idioma. Podrás cambiarlo más adelante en Ajustes.',
    ca: 'Tria el teu idioma. El podràs canviar més endavant a Ajustos.',
    en: 'Choose your language. You can change it later in Settings.',
  },
  'onboarding.modeTitle': { es: '¿En qué punto estás?', ca: 'En quin punt estàs?', en: 'Where are you at?' },
  'onboarding.modeSub': {
    es: 'Esto cambia qué te muestra la app.',
    ca: 'Això canvia què et mostra l’app.',
    en: 'This changes what the app shows you.',
  },
  'onboarding.modeQuitTitle': { es: 'Quiero dejarlo', ca: 'Vull deixar-ho', en: 'I want to quit' },
  'onboarding.modeQuitDesc': {
    es: 'Estás en ello ahora mismo, con o sin pastillas.',
    ca: 'Hi estàs ara mateix, amb o sense pastilles.',
    en: 'You’re on it right now, with or without pills.',
  },
  'onboarding.modeQuitBadge': { es: 'Lo más habitual', ca: 'El més habitual', en: 'Most common' },
  'onboarding.modeSmokingTitle': { es: 'Me planteo dejarlo', ca: 'M’ho plantejo deixar', en: 'I’m thinking about quitting' },
  'onboarding.modeSmokingDesc': {
    es: 'Todavía fumas. Empieza registrando y reduciendo, y marca tu fecha cuando quieras.',
    ca: 'Encara fumes. Comença registrant i reduint, i marca la teva data quan vulguis.',
    en: 'You still smoke. Start by tracking and cutting down, and set your date when you’re ready.',
  },
  'onboarding.modeMaintainTitle': { es: 'Ya lo dejé', ca: 'Ja ho vaig deixar', en: 'I already quit' },
  'onboarding.modeMaintainDesc': {
    es: 'No fumas y quieres mantenerlo así.',
    ca: 'No fumes i vols mantenir-ho així.',
    en: 'You don’t smoke and want to keep it that way.',
  },

  'onboarding.quitDateTitle': { es: '¿Desde cuándo no fumas?', ca: 'Des de quan no fumes?', en: 'How long since you smoked?' },
  'onboarding.quitDateSub': {
    es: 'Desde ese día contamos tu racha, tu ahorro y tu recuperación.',
    ca: 'Des d’aquell dia comptem la teva ratxa, el teu estalvi i la teva recuperació.',
    en: 'From that day we count your streak, your savings and your recovery.',
  },
  'onboarding.presetToday': { es: 'Hoy', ca: 'Avui', en: 'Today' },
  'onboarding.presetWeek': { es: 'Hace 1 semana', ca: 'Fa 1 setmana', en: '1 week ago' },
  'onboarding.presetMonth': { es: 'Hace 1 mes', ca: 'Fa 1 mes', en: '1 month ago' },
  'onboarding.quitDateNote': {
    es: 'Puedes retroceder todo lo que quieras: si lo dejaste hace años, la app lo refleja.',
    ca: 'Pots retrocedir tant com vulguis: si ho vas deixar fa anys, l’app ho reflecteix.',
    en: 'You can go back as far as you like: if you quit years ago, the app reflects it.',
  },

  'onboarding.methodTitle': { es: '¿Cómo lo estás haciendo?', ca: 'Com ho estàs fent?', en: 'How are you doing it?' },
  'onboarding.methodSub': {
    es: 'Si estás con pastillas, la app te lleva la cuenta de las tomas.',
    ca: 'Si estàs amb pastilles, l’app et porta el compte de les preses.',
    en: 'If you’re on pills, the app keeps track of your doses.',
  },
  'onboarding.methodColdTitle': { es: 'Por mi cuenta', ca: 'Pel meu compte', en: 'On my own' },
  'onboarding.methodColdDesc': { es: 'Sin medicación.', ca: 'Sense medicació.', en: 'Without medication.' },
  'onboarding.methodCytisineTitle': { es: 'Con pastillas de 25 días', ca: 'Amb pastilles de 25 dies', en: 'With 25-day pills' },
  'onboarding.methodCytisineDesc': {
    es: 'Recigarum o similar (citisiniclina). 100 comprimidos.',
    ca: 'Recigarum o similar (citisiniclina). 100 comprimits.',
    en: 'Recigarum or similar (cytisine). 100 tablets.',
  },

  'onboarding.treatmentTitle': { es: '¿Por qué día vas?', ca: 'Per quin dia vas?', en: 'Which day are you on?' },
  'onboarding.treatmentSub': {
    es: 'El día 1 es el primer día que tomaste una pastilla. Si empiezas hoy, es el 1.',
    ca: 'El dia 1 és el primer dia que vas prendre una pastilla. Si comences avui, és l’1.',
    en: 'Day 1 is the first day you took a pill. If you start today, it’s day 1.',
  },
  'onboarding.brandQuestion': { es: '¿Cómo se llama tu medicamento?', ca: 'Com es diu el teu medicament?', en: 'What’s your medication called?' },
  'onboarding.brandOther': { es: 'Otro', ca: 'Altre', en: 'Other' },
  'onboarding.brandPlaceholder': {
    es: 'Nombre de tu citisina / citisiniclina',
    ca: 'Nom de la teva citisina / citisiniclina',
    en: 'Name of your cytisine',
  },
  'onboarding.brandAria': { es: 'Nombre de tu medicamento', ca: 'Nom del teu medicament', en: 'Name of your medication' },
  'onboarding.brandNote': {
    es: 'Recigarum y Todacitan son citisina con la misma pauta de 25 días. Si tomas otra marca, escríbela y saldrá esa en la app.',
    ca: 'Recigarum i Todacitan són citisina amb la mateixa pauta de 25 dies. Si prens una altra marca, escriu-la i sortirà aquesta a l’app.',
    en: 'Recigarum and Todacitan are cytisine with the same 25-day schedule. If you take another brand, type it and it’ll show in the app.',
  },
  'onboarding.firstDoseLabel': {
    es: '¿A qué hora tomas la primera pastilla del día?',
    ca: 'A quina hora prens la primera pastilla del dia?',
    en: 'What time do you take the first pill of the day?',
  },
  'onboarding.treatmentEndsPre': { es: 'Terminas el', ca: 'Acabes el', en: 'You finish on' },
  'onboarding.treatmentEndsMid': {
    es: '. El prospecto marca dejar de fumar como muy tarde el día 5, que en tu caso es el',
    ca: '. El prospecte marca deixar de fumar com a molt tard el dia 5, que en el teu cas és el',
    en: '. The leaflet says to stop smoking by day 5 at the latest, which for you is',
  },
  'onboarding.disclaimer': {
    es: 'Respiria organiza tu tratamiento, pero no sustituye a tu médico ni al prospecto.',
    ca: 'Respiria organitza el teu tractament, però no substitueix el teu metge ni el prospecte.',
    en: 'Respiria organises your treatment, but it does not replace your doctor or the leaflet.',
  },

  'onboarding.tobaccoTitle': { es: 'Tu tabaco', ca: 'El teu tabac', en: 'Your tobacco' },
  'onboarding.tobaccoSub': {
    es: 'Sirve para calcular lo que ahorras. Puedes afinarlo luego.',
    ca: 'Serveix per calcular el que estalvies. Ho pots afinar després.',
    en: 'Used to work out your savings. You can fine-tune it later.',
  },
  'onboarding.packTitle': { es: 'Cajetilla', ca: 'Paquet', en: 'Pack' },
  'onboarding.packDesc': { es: 'De paquete.', ca: 'De paquet.', en: 'Boxed.' },
  'onboarding.rollingTitle': { es: 'De liar', ca: 'De liar', en: 'Rolling' },
  'onboarding.rollingDesc': { es: 'Picadura.', ca: 'Picadura.', en: 'Loose tobacco.' },
  'onboarding.iqosTitle': { es: 'IQOS', ca: 'IQOS', en: 'IQOS' },
  'onboarding.iqosDesc': { es: 'Calentado.', ca: 'Escalfat.', en: 'Heated.' },
  // «Otros» era un cajón de sastre al que se le preguntaba por paquetes y
  // unidades, que para un vaper no significan nada. Ahora son dos tipos con
  // nombre propio y una sola pregunta: lo que se dejaban a la semana.
  'onboarding.vapeTitle': { es: 'Vaper', ca: 'Vaper', en: 'Vape' },
  'onboarding.vapeDesc': { es: 'Cigarrillo electrónico', ca: 'Cigarret electrònic', en: 'E-cigarette' },
  'onboarding.mixedTitle': { es: 'Mixto', ca: 'Mixt', en: 'Mixed' },
  'onboarding.mixedDesc': { es: 'De liar e industrial', ca: 'De cargolar i industrial', en: 'Roll-ups and packs' },
  'onboarding.weeklySpend': { es: '¿Cuánto te gastabas a la semana?', ca: 'Quant et gastaves a la setmana?', en: 'How much did you spend a week?' },
  'onboarding.weeklySpendHint': {
    es: 'A ojo vale. Sirve para calcular lo que ahorras.',
    ca: 'A ull ja va bé. Serveix per calcular el que estalvies.',
    en: 'A rough figure is fine. It’s what your savings are worked out from.',
  },
  'onboarding.sticksPerDay': { es: 'Sticks al día', ca: 'Sticks al dia', en: 'Sticks per day' },
  'onboarding.cigsPerDay': { es: 'Cigarros al día', ca: 'Cigarrets al dia', en: 'Cigarettes per day' },
  'onboarding.pouchGrams': { es: 'Gramos por bolsa', ca: 'Grams per bossa', en: 'Grams per pouch' },
  'onboarding.pouchPrice': { es: 'Precio de la bolsa', ca: 'Preu de la bossa', en: 'Pouch price' },
  'onboarding.accessories': { es: 'Papel y filtros por bolsa', ca: 'Paper i filtres per bossa', en: 'Paper and filters per pouch' },
  'onboarding.sticksPerPack': { es: 'Sticks por paquete', ca: 'Sticks per paquet', en: 'Sticks per pack' },
  'onboarding.cigsPerPack': { es: 'Cigarros por cajetilla', ca: 'Cigarrets per paquet', en: 'Cigarettes per pack' },
  'onboarding.packPrice': { es: 'Precio de la cajetilla', ca: 'Preu del paquet', en: 'Pack price' },
  'onboarding.packPriceIqos': { es: 'Precio del paquete', ca: 'Preu del paquet', en: 'Pack price' },

  'onboarding.householdTitle': { es: 'Tu casa', ca: 'La teva casa', en: 'Your home' },
  'onboarding.householdSub': {
    es: 'Sirve para proponerte cosas que puedes hacer en casa durante un antojo. Opcional.',
    ca: 'Serveix per proposar-te coses que pots fer a casa durant unes ganes. Opcional.',
    en: 'Used to suggest things you can do at home during a craving. Optional.',
  },
  'onboarding.petQuestion': { es: '¿Tienes mascota?', ca: 'Tens mascota?', en: 'Do you have a pet?' },
  'onboarding.petNo': { es: 'No', ca: 'No', en: 'No' },
  'onboarding.petDog': { es: 'Perro', ca: 'Gos', en: 'Dog' },
  'onboarding.petCat': { es: 'Gato', ca: 'Gat', en: 'Cat' },
  'onboarding.petOther': { es: 'Otra', ca: 'Altra', en: 'Other' },
  'onboarding.petNamePlaceholder': { es: '¿Cómo se llama? (opcional)', ca: 'Com es diu? (opcional)', en: 'What’s its name? (optional)' },
  'onboarding.petNameAria': { es: 'Nombre de tu mascota', ca: 'Nom de la teva mascota', en: 'Your pet’s name' },
  'onboarding.householdQuestion': { es: '¿Con quién y con qué vives?', ca: 'Amb qui i amb què vius?', en: 'Who and what do you live with?' },

  'onboarding.doneTitle': { es: 'Todo listo', ca: 'Tot a punt', en: 'All set' },
  'onboarding.doneSub': {
    es: 'Esto es lo que dejarás de gastar. A partir de aquí, la app hace el resto.',
    ca: 'Això és el que deixaràs de gastar. A partir d’aquí, l’app fa la resta.',
    en: 'This is what you’ll stop spending. From here, the app does the rest.',
  },
  'onboarding.perDayLabel': { es: 'Al día', ca: 'Al dia', en: 'Per day' },
  'onboarding.perMonth': { es: 'Al mes', ca: 'Al mes', en: 'Per month' },
  'onboarding.perYear': { es: 'Al año', ca: 'A l’any', en: 'Per year' },
  'onboarding.dosesNote': {
    es: 'Te crearemos las {n} tomas del tratamiento con sus horas, desde el día {day}.',
    ca: 'Et crearem les {n} preses del tractament amb les seves hores, des del dia {day}.',
    en: 'We’ll create the treatment’s {n} doses with their times, from day {day}.',
  },

  // Navegación (aria)
  'nav.ariaMain': { es: 'Navegación principal', ca: 'Navegació principal', en: 'Main navigation' },
  'nav.craving.aria': {
    es: 'Tengo ganas de fumar. Mantén pulsado para el modo emergencia.',
    ca: 'Tinc ganes de fumar. Mantén premut per al mode d’emergència.',
    en: 'I feel like smoking. Press and hold for emergency mode.',
  },

  // Registro manual (LogSheet)
  'log.relapseTitle': { es: 'Registrar una recaída', ca: 'Registrar una recaiguda', en: 'Log a relapse' },
  'log.relapseIntro': {
    es: 'Una recaída es volver a fumar de forma habitual, no un cigarro suelto. Nada de lo que llevas se pierde: los logros, el dinero y tu mejor racha se quedan como están.',
    ca: 'Una recaiguda és tornar a fumar de manera habitual, no un cigarret solt. No es perd res del que portes: els assoliments, els diners i la teva millor ratxa es queden com estan.',
    en: 'A relapse means going back to smoking regularly, not one loose cigarette. Nothing you’ve built is lost: your achievements, your money and your best streak stay as they are.',
  },
  'log.relapseYes': { es: 'Sí, es una recaída', ca: 'Sí, és una recaiguda', en: 'Yes, it’s a relapse' },
  'log.wasSlip': { es: 'Ha sido solo un desliz', ca: 'Ha estat només una relliscada', en: 'It was just a slip' },
  'log.whatHappened': { es: '¿Qué ha pasado?', ca: 'Què ha passat?', en: 'What happened?' },
  'log.slipTitle': { es: 'He tenido un desliz', ca: 'He tingut una relliscada', en: 'I had a slip' },
  'log.slipDesc': {
    es: 'Un cigarro suelto. Tu racha se reinicia, nada más.',
    ca: 'Un cigarret solt. La teva ratxa es reinicia, res més.',
    en: 'One loose cigarette. Your streak resets, that’s all.',
  },
  'log.relapseChoice': { es: 'Quiero registrar una recaída', ca: 'Vull registrar una recaiguda', en: 'I want to log a relapse' },
  'log.relapseChoiceDesc': {
    es: 'He vuelto a fumar de forma habitual.',
    ca: 'He tornat a fumar de manera habitual.',
    en: 'I’ve gone back to smoking regularly.',
  },
  'log.savedTitle': { es: 'Registrado', ca: 'Registrat', en: 'Logged' },
  'log.savedReduction': {
    es: 'Anotado. Estás en la fase de bajar el consumo, y esto es parte del plan.',
    ca: 'Anotat. Estàs en la fase de baixar el consum, i això forma part del pla.',
    en: 'Noted. You’re in the cutting-down phase, and this is part of the plan.',
  },
  'log.savedSlip': {
    es: 'Un cigarro es un tropiezo, no el final del camino. Todo lo que llevas ahorrado sigue ahí.',
    ca: 'Un cigarret és un ensopec, no el final del camí. Tot el que portes estalviat continua aquí.',
    en: 'One cigarette is a stumble, not the end of the road. Everything you’ve saved is still there.',
  },
  'log.stillAvoided': {
    es: 'Sigues llevando {count} cigarros que no te has fumado. Eso no se borra.',
    ca: 'Encara portes {count} cigarrets que no t’has fumat. Això no s’esborra.',
    en: 'You’re still up {count} cigarettes you haven’t smoked. That doesn’t get erased.',
  },
  'log.restartHint': {
    es: 'Si quieres, puedes poner el contador a cero desde hoy. Todo lo anterior se conserva.',
    ca: 'Si vols, pots posar el comptador a zero des d’avui. Tot l’anterior es conserva.',
    en: 'If you like, you can reset the counter from today. Everything before is kept.',
  },
  'log.restartToday': { es: 'Empezar de nuevo hoy', ca: 'Començar de nou avui', en: 'Start over today' },
  'log.notNow': { es: 'Ahora no', ca: 'Ara no', en: 'Not now' },
  'log.cigTitle': { es: 'Registrar un cigarro', ca: 'Registrar un cigarret', en: 'Log a cigarette' },
  'log.when': { es: '¿Cuándo?', ca: 'Quan?', en: 'When?' },
  'log.howMany': { es: 'Cuántos', ca: 'Quants', en: 'How many' },
  'log.trigger': { es: '¿Qué lo ha disparado?', ca: 'Què ho ha provocat?', en: 'What set it off?' },
  'log.mood': { es: '¿Cómo estás de ánimo?', ca: 'Com estàs d’ànim?', en: 'How’s your mood?' },
  'log.save': { es: 'Registrar', ca: 'Registrar', en: 'Log it' },
  'log.onlyTime': { es: 'Solo la hora es necesaria.', ca: 'Només l’hora és necessària.', en: 'Only the time is required.' },

  // Duración larga (hitos, tiempos relativos). {n} = cantidad.
  'dur.year': { es: '{n} año', ca: '{n} any', en: '{n} year' },
  'dur.years': { es: '{n} años', ca: '{n} anys', en: '{n} years' },
  'dur.month': { es: '{n} mes', ca: '{n} mes', en: '{n} month' },
  'dur.months': { es: '{n} meses', ca: '{n} mesos', en: '{n} months' },
  'dur.week': { es: '{n} semana', ca: '{n} setmana', en: '{n} week' },
  'dur.weeks': { es: '{n} semanas', ca: '{n} setmanes', en: '{n} weeks' },
  'dur.day': { es: '{n} día', ca: '{n} dia', en: '{n} day' },
  'dur.days': { es: '{n} días', ca: '{n} dies', en: '{n} days' },
  'dur.hour': { es: '{n} hora', ca: '{n} hora', en: '{n} hour' },
  'dur.hours': { es: '{n} horas', ca: '{n} hores', en: '{n} hours' },
  'dur.minute': { es: '{n} minuto', ca: '{n} minut', en: '{n} minute' },
  'dur.minutes': { es: '{n} minutos', ca: '{n} minuts', en: '{n} minutes' },
  'dur.seconds': { es: 'unos segundos', ca: 'uns segons', en: 'a few seconds' },

  // Tiempo relativo. {duration} = una duración ya formateada (dur.*).
  'rel.now': { es: 'ahora mismo', ca: 'ara mateix', en: 'right now' },
  'rel.justNow': { es: 'hace un momento', ca: 'fa un moment', en: 'a moment ago' },
  'rel.in': { es: 'en {duration}', ca: 'd’aquí a {duration}', en: 'in {duration}' },
  'rel.ago': { es: 'hace {duration}', ca: 'fa {duration}', en: '{duration} ago' },

  // Estado de conexión (components/ConnectionStatus)
  'conn.offline': { es: 'Sin conexión · se subirá al reconectar', ca: 'Sense connexió · es pujarà en reconnectar', en: 'Offline · will upload when you reconnect' },
  'conn.offlinePending': { es: 'Sin conexión · {n} por subir', ca: 'Sense connexió · {n} per pujar', en: 'Offline · {n} to upload' },
  'conn.syncing': { es: 'Sincronizando {n}…', ca: 'Sincronitzant {n}…', en: 'Syncing {n}…' },
  'conn.synced': { es: 'Sincronizado', ca: 'Sincronitzat', en: 'Synced' },

  // Gráficas
  'chart.noData': { es: 'Todavía no hay datos suficientes.', ca: 'Encara no hi ha prou dades.', en: 'Not enough data yet.' },

  // Etiquetas de accesibilidad del inicio
  'dash.ariaReduction': { es: 'Consumo de hoy respecto a tu media habitual', ca: 'Consum d’avui respecte a la teva mitjana habitual', en: 'Today’s smoking compared with your usual average' },
  'dash.ariaHealth': { es: 'Progreso de recuperación', ca: 'Progrés de recuperació', en: 'Recovery progress' },
  'dash.ariaLevel': { es: 'Progreso de nivel', ca: 'Progrés de nivell', en: 'Level progress' },

  // Errores de activación de avisos (lib/push devuelve la clave, no el texto)
  'notif.errDenied': { es: 'Has bloqueado los avisos. Para activarlos hay que cambiarlo en los ajustes del navegador.', ca: 'Has bloquejat els avisos. Per activar-los cal canviar-ho als ajustos del navegador.', en: 'You’ve blocked notifications. To turn them on you need to change it in your browser settings.' },
  'notif.errNoPermission': { es: 'Sin permiso no puedo avisarte.', ca: 'Sense permís no puc avisar-te.', en: 'Without permission I can’t notify you.' },
  'notif.errRegister': { es: 'No he podido registrar el dispositivo.', ca: 'No he pogut registrar el dispositiu.', en: 'I couldn’t register this device.' },

  // Minijuego de los pulmones (modo emergencia)
  'game.hint': { es: 'Pulsa para soplar. Sin prisa.', ca: 'Toca per bufar. Sense pressa.', en: 'Tap to blow. No rush.' },
  'game.ariaBlow': { es: 'Soplar', ca: 'Bufar', en: 'Blow' },
  'game.ariaProgress': { es: 'Progreso hasta el siguiente nivel', ca: 'Progrés fins al següent nivell', en: 'Progress to the next level' },
  'game.blows': { es: '{n} soplidos', ca: '{n} bufades', en: '{n} blows' },
  'game.blowsOne': { es: '1 soplido', ca: '1 bufada', en: '1 blow' },
  'game.toNext': { es: 'te faltan {n} para el nivel {level}', ca: 'te’n falten {n} per al nivell {level}', en: '{n} to go for level {level}' },
  'game.toNextOne': { es: 'te falta 1 para el nivel {level}', ca: 'te’n falta 1 per al nivell {level}', en: '1 to go for level {level}' },
  'game.stage1': { es: 'Empieza a soplar', ca: 'Comença a bufar', en: 'Start blowing' },
  'game.stage2': { es: 'Va entrando aire', ca: 'Va entrant aire', en: 'Air is coming in' },
  'game.stage3': { es: 'Algo está creciendo', ca: 'Alguna cosa creix', en: 'Something is growing' },
  'game.stage4': { es: 'Se está limpiando', ca: 'S’està netejant', en: 'It’s clearing up' },
  'game.stage5': { es: 'Respira mejor', ca: 'Respira millor', en: 'Breathing easier' },
  'game.stage6': { es: 'Aire limpio', ca: 'Aire net', en: 'Clean air' },
};

export type TranslateKey = keyof typeof STRINGS;

// es/ca/en are inline above (the baseline every user gets). The rest live in
// their own files and are loaded on demand for the active language only, so a
// Spanish user never downloads Turkish (issue #38 §3). ca-valencia inherits ca,
// so its own overrides load only when Valencian is the language.
type LocaleTable = Record<string, Partial<Record<Language, string>>>;

const LOCALE_FILES: Record<string, () => Promise<LocaleTable>> = {
  extra: () => import('./locales-extra').then((m) => m.EXTRA_LOCALES), // hu/eu/it
  tr: () => import('./locales-tr').then((m) => m.TR_LOCALES),
  valencia: () => import('./locales-valencia').then((m) => m.VALENCIA_LOCALES),
};

function fileFor(lang: Language): keyof typeof LOCALE_FILES | null {
  if (lang === 'hu' || lang === 'eu' || lang === 'it') return 'extra';
  if (lang === 'tr') return 'tr';
  if (lang === 'ca-valencia') return 'valencia';
  return null; // es/ca/en are already in the baseline
}

const loadedFiles = new Set<string>();
const loadingFiles = new Map<string, Promise<void>>();

/**
 * Loads and merges the locale file for `lang`, once. Idempotent and safe to
 * call fire-and-forget: when it lands it bumps the i18n version so `useT`
 * consumers re-render with the now-available strings (until then they fall back
 * to Spanish, never a blank or a raw key).
 */
export function loadLocale(lang: Language): Promise<void> {
  const file = fileFor(lang);
  if (!file || loadedFiles.has(file)) return Promise.resolve();
  const inFlight = loadingFiles.get(file);
  if (inFlight) return inFlight;
  const importFile = LOCALE_FILES[file];
  if (!importFile) return Promise.resolve();

  const p = importFile()
    .then((table) => {
      for (const key in table) {
        const entry = STRINGS[key];
        if (entry) Object.assign(entry, table[key]);
      }
      loadedFiles.add(file);
      loadingFiles.delete(file);
      notifyI18n();
    })
    .catch(() => {
      // Offline or a bad chunk: keep the Spanish fallback rather than crash.
      loadingFiles.delete(file);
    });
  loadingFiles.set(file, p);
  return p;
}

/**
 * Runtime UI overrides fetched from the server (issue #14): { [lang]: { [key]: value } }.
 * They win over the static catalogue and take part in the same fallback chain, so
 * an admin can correct any string — Valencian especially — without a redeploy.
 */
let RUNTIME_OVERRIDES: Record<string, Record<string, string>> = {};
let overridesVersion = 0;
const overrideListeners = new Set<() => void>();

/** Replace the override set and notify subscribers (see `subscribeOverrides`). */
export function applyUiOverrides(overrides: Record<string, Record<string, string>>): void {
  RUNTIME_OVERRIDES = overrides ?? {};
  overridesVersion++;
  for (const listener of overrideListeners) listener();
}

export function subscribeOverrides(listener: () => void): () => void {
  overrideListeners.add(listener);
  return () => overrideListeners.delete(listener);
}

export function getOverridesVersion(): number {
  return overridesVersion;
}

function notifyI18n(): void {
  overridesVersion++;
  for (const listener of overrideListeners) listener();
}

const SUPPORTED = LANGUAGES.map((l) => l.code);

/** Best supported match for the device language (e.g. 'ca-ES' → 'ca'). */
function detectSystemLanguage(): Language {
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'es')?.toLowerCase() ?? 'es';
  if (SUPPORTED.includes(nav as Language)) return nav as Language;
  const base = nav.split('-')[0];
  return (SUPPORTED.find((l) => l === base) as Language | undefined) ?? 'es';
}

const LANG_KEY = 'respiria.lang';

function readStoredLanguage(): Language | null {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return v && SUPPORTED.includes(v as Language) ? (v as Language) : null;
  } catch {
    return null;
  }
}

/**
 * The language to use before the account's settings load (login, onboarding,
 * demo): a previous choice, else the device language. Once settings exist,
 * `useT` prefers `settings.language`.
 */
let preferredLanguage: Language = readStoredLanguage() ?? detectSystemLanguage();

export function getPreferredLanguage(): Language {
  return preferredLanguage;
}

export function setPreferredLanguage(lang: Language): void {
  preferredLanguage = lang;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Private mode / storage blocked — the in-memory value still applies.
  }
  // Fetch the new language's strings (no-op for es/ca/en); loadLocale notifies
  // again when they land, so notify now too for the immediate baseline switch.
  void loadLocale(lang);
  notifyI18n();
}

/**
 * Idioma de la landing pública (#78), que **no sigue la misma regla que la app**.
 *
 * Decisión del usuario: castellano para España y para las variantes de Latinoamérica,
 * valenciano para los locales catalanes, e **inglés para todo lo demás**. La app cae a
 * castellano cuando no reconoce el idioma; la portada no puede, porque quien llega de fuera
 * no la entendería.
 *
 * El euskera y el gallego caen en castellano a propósito: son lenguas oficiales de España, y
 * la regla era que el inglés fuera para quien está fuera. Cuando la portada se traduzca a
 * más idiomas, este es el sitio donde se amplía.
 */
export function detectLandingLanguage(): Language {
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'es')?.toLowerCase() ?? 'es';
  if (nav.startsWith('ca') || nav.startsWith('va')) return 'ca-valencia';
  if (nav.startsWith('es') || nav.startsWith('eu') || nav.startsWith('gl')) return 'es';
  return 'en';
}

/**
 * Fija el idioma de la sesión **sin guardarlo** como si el visitante lo hubiera elegido.
 * La landing detecta; guardar esa detección haría que un cambio de idioma del móvil ya no se
 * notara nunca más, y convertiría una suposición en una preferencia.
 */
export function applyDetectedLanguage(lang: Language): void {
  if (readStoredLanguage() !== null || preferredLanguage === lang) return;
  preferredLanguage = lang;
  void loadLocale(lang);
  notifyI18n();
}

/** All catalogue keys (admin translation editor, issue #14). */
export const CATALOGUE_KEYS: string[] = Object.keys(STRINGS);

/**
 * The static (pre-override) value for a key in a locale, with the same fallback
 * chain as `translate`. Used by the admin editor to show the default a viewer
 * would get today, so an override is only entered where it should differ.
 */
export function staticValue(lang: Language, key: string): string {
  const entry = STRINGS[key];
  return entry?.[lang] ?? entry?.[FALLBACK[lang] ?? 'es'] ?? entry?.es ?? key;
}

/** Whether the catalogue has its own string for this exact locale (not inherited). */
export function hasStaticExact(lang: Language, key: string): boolean {
  return STRINGS[key]?.[lang] != null;
}

/** Resolve a key for a locale (with fallback), then interpolate `{params}`. */
export function translate(
  lang: Language,
  key: string,
  params?: Record<string, string | number>,
): string {
  const entry = STRINGS[key];
  const resolve = (l: Language): string | undefined => RUNTIME_OVERRIDES[l]?.[key] ?? entry?.[l];
  const raw = resolve(lang) ?? resolve(FALLBACK[lang] ?? 'es') ?? resolve('es') ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in params ? String(params[name]) : `{${name}}`,
  );
}
